import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Shield, FileText, Image, History, User, LogOut, Loader2, CheckCircle, AlertTriangle, Upload, X, Menu, ChevronRight, ShieldAlert, AlertOctagon, Mic, Video, Play, CreditCard, Coins, Zap, Package, ArrowRight, ScrollText, Lock, AlertCircle, Scale, Users, Share2, DollarSign, Copy, Gift, TrendingUp, Wallet, Link2, ExternalLink, Globe, Settings, BarChart3, UserCog, Receipt, Network, Search, Plus, Minus, Eye, Bug, FileSearch, HelpCircle } from "lucide-react";
import LandingPage from "./components/LandingPage";

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// App Info
const APP_INFO = {
  name: "WISE EAGLE",
  version: "1.0.0",
  company: "WISE EAGLE",
  email: "contato@wiseeagle.online",
  lastUpdate: "12/02/2026"
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);

  const fetchCredits = async (authToken) => {
    try {
      const res = await axios.get(`${API}/credits`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setCredits(res.data.credits);
    } catch (err) {
      console.error("Error fetching credits:", err);
    }
  };

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(res => {
          setUser(res.data);
          fetchCredits(token);
        })
        .catch(() => {
          localStorage.removeItem("token");
          setToken(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    fetchCredits(res.data.token);
    return res.data;
  };

  const register = async (name, email, password, referralCode = null) => {
    const payload = { name, email, password };
    if (referralCode) {
      payload.referral_code = referralCode;
    }
    const res = await axios.post(`${API}/auth/register`, payload);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setCredits(0);
  };

  const refreshCredits = () => {
    if (token) {
      fetchCredits(token);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, credits, login, register, logout, loading, refreshCredits }}>
      {children}
    </AuthContext.Provider>
  );
};

// Header Component
const Header = ({ onShowHistory, onShowCredits, onShowReferral, onShowAdmin, onBackToDashboard, showingSubPage, isAdmin }) => {
  const { user, credits, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <header className="header">
      <div className="main-content py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={onBackToDashboard}>
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
            <p className="text-xs text-zinc-400">Proteja-se contra conteúdo falso</p>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-3">
          {/* Admin Button - Only show if user is admin */}
          {isAdmin && (
            <button
              data-testid="admin-btn"
              onClick={onShowAdmin}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 hover:border-red-500/50 transition-colors text-red-300 hover:text-red-200"
            >
              <UserCog className="w-4 h-4" />
              Admin
            </button>
          )}

          {/* Credits Display */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
            <Coins className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-300">{credits} créditos</span>
          </div>
          
          {/* Buy Credits Button */}
          <button
            data-testid="buy-credits-btn"
            onClick={onShowCredits}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 transition-all text-white font-medium text-sm shadow-lg shadow-cyan-500/25"
          >
            <CreditCard className="w-4 h-4" />
            Comprar Créditos
          </button>

          {/* Referral Button */}
          <button
            data-testid="referral-btn"
            onClick={onShowReferral}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 hover:border-emerald-500/50 transition-colors text-emerald-300 hover:text-emerald-200"
          >
            <Users className="w-4 h-4" />
            Indicar
          </button>
          
          <button
            data-testid="history-btn"
            onClick={onShowHistory}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-800 transition-colors text-zinc-300 hover:text-white"
          >
            <History className="w-4 h-4" />
            Histórico
          </button>
          <div className="flex items-center gap-3 pl-3 border-l border-zinc-700">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center">
                <User className="w-4 h-4 text-zinc-300" />
              </div>
              <span className="text-sm text-zinc-300">{user?.name}</span>
            </div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="p-2 rounded-lg hover:bg-zinc-800 transition-colors text-zinc-400 hover:text-white"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-zinc-800"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Menu - Compact dropdown with animation */}
      {menuOpen && (
        <div className="md:hidden absolute right-4 top-16 w-64 bg-zinc-900/95 backdrop-blur-sm border border-zinc-700 rounded-xl shadow-2xl z-50 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="px-4 py-3 border-b border-zinc-700/50">
            <p className="text-sm text-zinc-400">Olá, <span className="font-semibold text-white">{user?.name}</span></p>
          </div>
          <div className="p-2 space-y-1">
            {showingSubPage && (
              <button
                onClick={() => { onBackToDashboard(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-sm transition-all hover:scale-[1.02]"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                Voltar ao Dashboard
              </button>
            )}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-300 text-sm">
              <Coins className="w-4 h-4" />
              {credits} créditos
            </div>
            <button
              onClick={() => { onShowCredits(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium transition-all"
            >
              <CreditCard className="w-4 h-4" />
              Comprar Créditos
            </button>
            <button
              onClick={() => { onShowReferral(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-sm transition-all"
            >
              <Users className="w-4 h-4" />
              Indicar e Ganhar
            </button>
            <button
              onClick={() => { onShowHistory(); setMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-800 text-zinc-200 text-sm transition-all hover:translate-x-1"
            >
              <History className="w-4 h-4" />
              Histórico
            </button>
            {isAdmin && (
              <button
                onClick={() => { onShowAdmin(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm transition-all"
              >
                <UserCog className="w-4 h-4" />
                Painel Admin
              </button>
            )}
            <div className="border-t border-zinc-700/50 mt-1 pt-1">
              <button
                onClick={() => { logout(); setMenuOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-zinc-800 text-red-400 text-sm transition-all hover:translate-x-1"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// Auth Page (Login/Register)
const AuthPage = ({ onBackToLanding }) => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showLegalPage, setShowLegalPage] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [registrationEmail, setRegistrationEmail] = useState("");
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [showResetCodeInput, setShowResetCodeInput] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // Check for referral code in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    
    if (ref) {
      setReferralCode(ref.toUpperCase());
      setIsLogin(false);
    }
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setResetEmail(email);
      setShowResetCodeInput(true);
      setShowForgotPassword(false);
      setSuccessMessage("Se o email existir em nossa base, você receberá um código de recuperação. Verifique sua caixa de entrada e spam.");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao processar solicitação");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    setError("");
    if (resetCode.length !== 6) {
      setError("Digite o código de 6 dígitos");
      return;
    }
    // Code is valid format, show password reset form
    setShowResetCodeInput(false);
    setShowResetPassword(true);
    setSuccessMessage("");
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    
    if (newPassword !== confirmPassword) {
      setError("As senhas não coincidem");
      return;
    }
    
    if (newPassword.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      });
      setSuccessMessage("Senha alterada com sucesso! Você já pode fazer login.");
      setShowResetPassword(false);
      setIsLogin(true);
      setResetCode("");
      setResetEmail("");
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao redefinir senha");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(name, email, password, referralCode || undefined);
        setRegistrationEmail(email);
        setShowRegistrationSuccess(true);
        setName("");
        setEmail("");
        setPassword("");
      }
      if (referralCode && !isLogin) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403) {
        setRegistrationEmail(email);
        setShowEmailVerification(true);
        setError("");
      } else {
        setError(detail || "Erro ao processar solicitação");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setError("");
    setVerifyLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-email`, {
        email: registrationEmail,
        code: verificationCode
      });
      setVerificationStatus(res.data.already_verified ? 'already' : 'success');
    } catch (err) {
      setError(err.response?.data?.detail || "Código inválido");
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: registrationEmail });
      setResendSuccess(true);
    } catch (err) {
      setResendSuccess(true);
    } finally {
      setResendLoading(false);
    }
  };

  // Show Email Verification (code input for unverified users trying to login)
  if (showEmailVerification) {
    if (verificationStatus === 'success' || verificationStatus === 'already') {
      return (
        <div className="min-h-screen hero-gradient flex flex-col">
          <header className="header">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
                <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
              </div>
            </div>
          </header>
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8 text-center" data-testid="email-verification-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">E-mail Confirmado!</h2>
              <p className="text-zinc-400 text-sm mb-6">Sua conta foi ativada com sucesso. Agora você pode fazer login.</p>
              <button
                data-testid="go-to-login-btn"
                onClick={() => { setShowEmailVerification(false); setVerificationStatus(null); setVerificationCode(""); setIsLogin(true); }}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                Fazer Login
              </button>
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen hero-gradient flex flex-col">
        <header className="header">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
              <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8 text-center" data-testid="email-verification-code">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">E-mail não confirmado</h2>
            <p className="text-zinc-400 text-sm mb-2">Digite o código de 6 dígitos enviado para:</p>
            <p className="text-cyan-400 font-medium mb-6">{registrationEmail}</p>
            
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <input
                data-testid="verification-code-input"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full p-4 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-zinc-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                placeholder="000000"
                maxLength={6}
                required
              />

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                data-testid="verify-code-btn"
                type="submit"
                disabled={verifyLoading || verificationCode.length !== 6}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {verifyLoading && <Loader2 className="w-4 h-4 spinner" />}
                Confirmar Código
              </button>
            </form>
            
            <div className="mt-4 space-y-2">
              <button
                data-testid="resend-code-btn"
                onClick={handleResendVerification}
                disabled={resendLoading || resendSuccess}
                className="text-sm text-zinc-400 hover:text-cyan-400 transition-colors"
              >
                {resendLoading ? 'Enviando...' : resendSuccess ? 'Novo código enviado!' : 'Reenviar código'}
              </button>
              <br />
              <button
                onClick={() => { setShowEmailVerification(false); setError(""); setVerificationCode(""); }}
                className="text-sm text-zinc-500 hover:text-zinc-300"
              >
                Voltar para o login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show Registration Success (email verification code input)
  if (showRegistrationSuccess) {
    if (verificationStatus === 'success') {
      return (
        <div className="min-h-screen hero-gradient flex flex-col">
          <header className="header">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
                <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
              </div>
            </div>
          </header>
          <main className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8 text-center" data-testid="registration-verified">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Conta Ativada!</h2>
              <p className="text-zinc-400 text-sm mb-6">Seu e-mail foi confirmado com sucesso. Agora você pode fazer login.</p>
              <button
                data-testid="go-to-login-after-verify-btn"
                onClick={() => { setShowRegistrationSuccess(false); setVerificationStatus(null); setVerificationCode(""); setIsLogin(true); }}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                Fazer Login
              </button>
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen hero-gradient flex flex-col">
        <header className="header">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
              <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
            </div>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8 text-center" data-testid="registration-success">
            <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-cyan-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Conta Criada!</h2>
            <p className="text-zinc-400 text-sm mb-2">
              Enviamos um código de verificação para:
            </p>
            <p className="text-cyan-400 font-medium mb-6">{registrationEmail}</p>
            
            <form onSubmit={handleVerifyEmail} className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Código de Verificação</label>
                <input
                  data-testid="register-verification-code-input"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full p-4 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-zinc-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  placeholder="000000"
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                data-testid="verify-register-code-btn"
                type="submit"
                disabled={verifyLoading || verificationCode.length !== 6}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {verifyLoading && <Loader2 className="w-4 h-4 spinner" />}
                Ativar Conta
              </button>
            </form>
            
            <div className="mt-4 pt-4 border-t border-zinc-800 space-y-2">
              <p className="text-xs text-zinc-500">Verifique sua caixa de entrada e spam</p>
              <button
                data-testid="resend-verification-btn"
                onClick={handleResendVerification}
                disabled={resendLoading || resendSuccess}
                className="text-sm text-zinc-400 hover:text-cyan-400 transition-colors"
              >
                {resendLoading ? 'Enviando...' : resendSuccess ? 'Novo código enviado!' : 'Reenviar código'}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show legal pages if selected
  if (showLegalPage === 'privacy') {
    return (
      <div className="min-h-screen hero-gradient p-4 md:p-8">
        <PrivacyPolicyPage onBack={() => setShowLegalPage(null)} />
      </div>
    );
  }
  if (showLegalPage === 'terms') {
    return (
      <div className="min-h-screen hero-gradient p-4 md:p-8">
        <TermsOfUsePage onBack={() => setShowLegalPage(null)} />
      </div>
    );
  }
  if (showLegalPage === 'lgpd') {
    return (
      <div className="min-h-screen hero-gradient p-4 md:p-8">
        <LGPDPage onBack={() => setShowLegalPage(null)} />
      </div>
    );
  }
  if (showLegalPage === 'disclaimer') {
    return (
      <div className="min-h-screen hero-gradient p-4 md:p-8">
        <DisclaimerPage onBack={() => setShowLegalPage(null)} />
      </div>
    );
  }
  if (showLegalPage === 'referral-program') {
    return (
      <div className="min-h-screen hero-gradient p-4 md:p-8">
        <ReferralProgramPage onBack={() => setShowLegalPage(null)} />
      </div>
    );
  }

  // Show Reset Password Form
  // Show Reset Password Form (after code verified)
  if (showResetPassword) {
    return (
      <div className="min-h-screen hero-gradient flex flex-col">
        <header className="header">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
              <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Redefinir Senha</h2>
              <p className="text-zinc-400 text-sm mt-2">Digite sua nova senha abaixo</p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Nova Senha</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Confirmar Senha</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  placeholder="Digite novamente"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                  {successMessage}
                </div>
              )}

              <button
                data-testid="reset-password-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 spinner" />}
                Redefinir Senha
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setError("");
                  setResetCode("");
                  setResetEmail("");
                }}
                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-300"
              >
                Voltar para o login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show Reset Code Input (after forgot password email sent)
  if (showResetCodeInput) {
    return (
      <div className="min-h-screen hero-gradient flex flex-col">
        <header className="header">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
              <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Código de Recuperação</h2>
              <p className="text-zinc-400 text-sm mt-2">
                Digite o código de 6 dígitos enviado para:
              </p>
              <p className="text-cyan-400 font-medium mt-1">{resetEmail}</p>
            </div>

            {successMessage && (
              <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleVerifyResetCode} className="space-y-4">
              <input
                data-testid="reset-code-input"
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full p-4 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-center text-2xl tracking-[0.5em] font-mono placeholder-zinc-600 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                placeholder="000000"
                maxLength={6}
                required
                autoFocus
              />

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                data-testid="verify-reset-code-btn"
                type="submit"
                disabled={resetCode.length !== 6}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                Continuar
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 mb-3 text-center">Verifique sua caixa de entrada e spam</p>
              <button
                onClick={() => {
                  setShowResetCodeInput(false);
                  setError("");
                  setSuccessMessage("");
                  setResetCode("");
                }}
                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-300"
              >
                Voltar para o login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show Forgot Password Form (email input)
  if (showForgotPassword) {
    return (
      <div className="min-h-screen hero-gradient flex flex-col">
        <header className="header">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden">
              <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
              <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md glass-panel rounded-2xl p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-cyan-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Esqueceu sua senha?</h2>
              <p className="text-zinc-400 text-sm mt-2">Digite seu email e enviaremos um código de recuperação</p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Email</label>
                <input
                  data-testid="forgot-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full p-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  placeholder="seu@email.com"
                  required
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                data-testid="send-recovery-code-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 spinner" />}
                Enviar Código de Recuperação
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setError("");
                  setSuccessMessage("");
                }}
                className="w-full text-center text-sm text-zinc-500 hover:text-zinc-300"
              >
                Voltar para o login
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen hero-gradient flex flex-col">
      {/* Header */}
      <header className="header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full overflow-hidden">
            <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">WISE EAGLE</h1>
            <p className="text-xs text-zinc-500">Proteja-se contra conteúdo falso</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Hero Text */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-2">
              {isLogin ? "Bem-vindo de volta!" : "Crie sua conta"}
            </h2>
            <p className="text-zinc-400">
              {isLogin 
                ? "Entre para acessar seu histórico de verificações" 
                : "Comece a detectar conteúdo falso agora"}
            </p>
          </div>

          {/* Auth Card */}
          <div className="glass-panel rounded-xl p-6 fade-in">
            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">Nome</label>
                  <input
                    data-testid="auth-name-input"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-input"
                    placeholder="Seu nome"
                    required={!isLogin}
                  />
                </div>
              )}
              
              {/* Show referral code badge when present */}
              {!isLogin && referralCode && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-300">
                    Código de indicação: <span className="font-mono font-bold">{referralCode}</span>
                  </span>
                  <span className="text-xs text-emerald-400/70 ml-auto">+5 créditos!</span>
                </div>
              )}

              <div>
                <label className="block text-sm text-zinc-400 mb-2">Email</label>
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="seu@email.com"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Senha</label>
                <input
                  data-testid="auth-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="Sua senha"
                  required
                />
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setError("");
                      setSuccessMessage("");
                    }}
                    className="mt-2 text-sm text-cyan-400 hover:text-cyan-300"
                  >
                    Esqueceu sua senha?
                  </button>
                )}
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {successMessage && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
                  {successMessage}
                </div>
              )}

              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 spinner" />}
                {isLogin ? "Entrar" : "Criar Conta"}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-zinc-800">
              <p className="text-center text-sm text-zinc-500">
                {isLogin ? "Não tem conta?" : "Já tem conta?"}{" "}
                <button
                  data-testid="auth-toggle-btn"
                  onClick={() => { setIsLogin(!isLogin); setError(""); }}
                  className="text-cyan-400 hover:text-cyan-300 font-medium"
                >
                  {isLogin ? "Criar conta grátis" : "Entrar"}
                </button>
              </p>
            </div>
          </div>

          {/* Features Cards */}
          <div className="mt-8 grid gap-3">
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Verificar Textos</h4>
                <p className="text-xs text-zinc-500">Detecte se um texto foi escrito por IA</p>
              </div>
            </div>
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                <Image className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Verificar Imagens</h4>
                <p className="text-xs text-zinc-500">Identifique imagens geradas por IA</p>
              </div>
            </div>
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                <Mic className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Verificar Áudios</h4>
                <p className="text-xs text-zinc-500">Detecte vozes clonadas ou sintéticas</p>
              </div>
            </div>
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center flex-shrink-0">
                <Video className="w-5 h-5 text-rose-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Verificar Vídeos</h4>
                <p className="text-xs text-zinc-500">Detecte deepfakes e vídeos de IA</p>
              </div>
            </div>
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Verificar Links</h4>
                <p className="text-xs text-zinc-500">Detecte links maliciosos e phishing</p>
              </div>
            </div>
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Bug className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Antivírus</h4>
                <p className="text-xs text-zinc-500">Escaneie arquivos com 70+ antivírus</p>
              </div>
            </div>
            <div className="glass-panel rounded-xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                <ShieldAlert className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h4 className="font-semibold text-sm text-zinc-200">Golpe Alert</h4>
                <p className="text-xs text-zinc-500">Detecte padrões de golpes e fraudes</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-zinc-800/30">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-zinc-600">
              {APP_INFO.name} © 2026 - Proteja-se contra conteúdo gerado por IA
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-zinc-500">
              <button onClick={() => setShowLegalPage('privacy')} className="hover:text-cyan-400 transition-colors">
                Privacidade
              </button>
              <span className="text-zinc-700">•</span>
              <button onClick={() => setShowLegalPage('terms')} className="hover:text-cyan-400 transition-colors">
                Termos
              </button>
              <span className="text-zinc-700">•</span>
              <button onClick={() => setShowLegalPage('lgpd')} className="hover:text-cyan-400 transition-colors">
                LGPD
              </button>
              <span className="text-zinc-700">•</span>
              <a href={`mailto:${APP_INFO.email}`} className="hover:text-cyan-400 transition-colors">
                Contato: {APP_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Auth Modal (kept for potential future use)
const AuthModal = ({ onClose }) => {
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(name, email, password);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao processar solicitação");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="auth-modal p-6 fade-in">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">{isLogin ? "Entrar" : "Criar Conta"}</h2>
          <button
            data-testid="close-auth-modal"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-zinc-800 text-zinc-400"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Nome</label>
              <input
                data-testid="auth-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
                placeholder="Seu nome"
                required={!isLogin}
              />
            </div>
          )}
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Email</label>
            <input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              placeholder="seu@email.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Senha</label>
            <input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              placeholder="Sua senha"
              required
            />
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            data-testid="auth-submit-btn"
            type="submit"
            disabled={loading}
            className="w-full btn-primary flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 spinner" />}
            {isLogin ? "Entrar" : "Criar Conta"}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-zinc-500">
          {isLogin ? "Não tem conta?" : "Já tem conta?"}{" "}
          <button
            data-testid="auth-toggle-btn"
            onClick={() => setIsLogin(!isLogin)}
            className="text-cyan-400 hover:text-cyan-300"
          >
            {isLogin ? "Criar conta" : "Entrar"}
          </button>
        </p>
      </div>
    </div>
  );
};

// Result Card
const ResultCard = ({ result, onClose }) => {
  const isHuman = !result.is_ai_generated;
  const confidence = result.confidence;
  const scamAnalysis = result.scam_analysis;
  
  // Determine confidence level for warnings
  const isLowConfidence = confidence < 60;
  const isMediumConfidence = confidence >= 60 && confidence < 75;
  
  // Get appropriate styling based on confidence and result
  const getResultStyle = () => {
    if (isLowConfidence) return 'uncertain';
    return isHuman ? 'human' : 'ai';
  };
  
  const getResultIcon = () => {
    if (isLowConfidence) {
      return (
        <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
        </div>
      );
    }
    if (isHuman) {
      return (
        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle className="w-6 h-6 text-green-500" />
        </div>
      );
    }
    return (
      <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
        <AlertTriangle className="w-6 h-6 text-red-500" />
      </div>
    );
  };
  
  const getResultTitle = () => {
    if (isLowConfidence) return "Resultado Inconclusivo";
    return isHuman ? "Provavelmente Humano" : "Provavelmente IA";
  };
  
  const getResultColor = () => {
    if (isLowConfidence) return 'text-amber-400';
    return isHuman ? 'text-green-400' : 'text-red-400';
  };

  const getScamRiskColor = (level) => {
    switch(level) {
      case 'critico': return 'text-red-500 bg-red-500/20 border-red-500/50';
      case 'alto': return 'text-orange-500 bg-orange-500/20 border-orange-500/50';
      case 'medio': return 'text-amber-500 bg-amber-500/20 border-amber-500/50';
      default: return 'text-green-500 bg-green-500/20 border-green-500/50';
    }
  };

  const getScamRiskLabel = (level) => {
    switch(level) {
      case 'critico': return 'CRÍTICO';
      case 'alto': return 'ALTO';
      case 'medio': return 'MÉDIO';
      default: return 'BAIXO';
    }
  };

  return (
    <div className="space-y-4 fade-in">
      {/* AI Detection Result */}
      <div className={`result-card ${getResultStyle()}`}>
        {/* Low Confidence Warning */}
        {isLowConfidence && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Texto muito curto para análise confiável</span>
            </div>
            <p className="text-xs text-amber-400/70 mt-1">
              Recomendamos textos com pelo menos 2-3 parágrafos para resultados mais precisos.
            </p>
          </div>
        )}
        
        {/* Medium Confidence Warning */}
        {isMediumConfidence && (
          <div className="mb-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <div className="flex items-center gap-2 text-zinc-300">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">Resultado com confiança moderada</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Considere analisar mais contexto para um resultado mais preciso.
            </p>
          </div>
        )}

        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {getResultIcon()}
            <div>
              <h3 className={`text-lg font-bold ${getResultColor()}`}>
                {getResultTitle()}
              </h3>
              <p className="text-sm text-zinc-400">
                Confiança: {confidence.toFixed(0)}%
              </p>
            </div>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800/50">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          )}
        </div>

        <div className="mb-4">
          <div className="confidence-bar">
            <div
              className={`confidence-fill ${getResultStyle()}`}
              style={{ width: `${confidence}%` }}
            />
          </div>
        </div>

        <div className="p-4 rounded-lg bg-zinc-900/50">
          <h4 className="text-sm font-semibold text-zinc-300 mb-2">Análise de IA:</h4>
          <p className="text-sm text-zinc-400 leading-relaxed">{result.analysis}</p>
        </div>
      </div>

      {/* Scam Analysis Result */}
      {scamAnalysis && (
        <div className={`result-card ${scamAnalysis.is_scam ? 'scam-danger' : 'scam-safe'}`} data-testid="scam-result">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${scamAnalysis.is_scam ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
                {scamAnalysis.is_scam ? (
                  <AlertOctagon className="w-6 h-6 text-red-500" />
                ) : (
                  <ShieldAlert className="w-6 h-6 text-green-500" />
                )}
              </div>
              <div>
                <h3 className={`text-lg font-bold ${scamAnalysis.is_scam ? 'text-red-400' : 'text-green-400'}`}>
                  {scamAnalysis.is_scam ? 'Possível Golpe Detectado!' : 'Nenhum Golpe Detectado'}
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 text-xs font-bold rounded border ${getScamRiskColor(scamAnalysis.risk_level)}`}>
                    RISCO {getScamRiskLabel(scamAnalysis.risk_level)}
                  </span>
                  <span className="text-sm text-zinc-400">
                    Score: {scamAnalysis.risk_score}%
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Bar */}
          <div className="mb-4">
            <div className="confidence-bar">
              <div
                className={`confidence-fill ${scamAnalysis.risk_level === 'baixo' ? 'human' : scamAnalysis.risk_level === 'medio' ? 'uncertain' : 'ai'}`}
                style={{ width: `${scamAnalysis.risk_score}%` }}
              />
            </div>
          </div>

          {/* Patterns Found */}
          {scamAnalysis.patterns_found && scamAnalysis.patterns_found.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-zinc-300 mb-2">Padrões Detectados:</h4>
              <div className="flex flex-wrap gap-2">
                {scamAnalysis.patterns_found.map((pattern, index) => (
                  <span key={index} className="px-2 py-1 text-xs rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">
                    {pattern}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Recommendation */}
          <div className={`p-4 rounded-lg ${scamAnalysis.is_scam ? 'bg-red-500/10 border border-red-500/30' : 'bg-green-500/10 border border-green-500/30'}`}>
            <h4 className="text-sm font-semibold text-zinc-300 mb-2">Recomendação:</h4>
            <p className={`text-sm leading-relaxed ${scamAnalysis.is_scam ? 'text-red-300' : 'text-green-300'}`}>
              {scamAnalysis.recommendation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Text Scanner
const TextScanner = ({ checkScam }) => {
  const { token, refreshCredits } = useAuth();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  
  const MAX_CHARS = 2500;

  const handleScan = async () => {
    if (text.trim().length < 50) {
      setError("O texto deve ter pelo menos 50 caracteres para análise confiável");
      return;
    }
    if (text.trim().length > MAX_CHARS) {
      setError(`O texto excede o limite de ${MAX_CHARS} caracteres. Por favor, reduza o tamanho.`);
      return;
    }
    setError("");
    setLoading(true);
    setResult(null);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/detect/text`, { text, check_scam: checkScam }, { headers });
      setResult(res.data);
      refreshCredits(); // Atualizar créditos automaticamente
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao analisar texto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="scanner-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-cyan-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Verificador de Texto</h2>
            <p className="text-sm text-zinc-400">Cole o texto suspeito para análise (máx. {MAX_CHARS} caracteres)</p>
          </div>
        </div>

        {/* Warning about limitations */}
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <p className="text-xs text-amber-300">
            <strong>Importante:</strong> Textos conversacionais curtos são mais difíceis de detectar, pois IAs modernas imitam linguagem humana informal. Para melhores resultados, use textos com 2 ou mais parágrafos.
          </p>
        </div>

        {/* Text Area with Clear Button */}
        <div className="relative mb-4">
          <textarea
            data-testid="text-input"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS + 100))}
            className={`w-full h-48 p-4 pr-12 rounded-lg bg-zinc-900 border-2 ${text.length > MAX_CHARS ? 'border-red-500' : 'border-cyan-500/50'} text-white placeholder-zinc-300 focus:outline-none focus:border-cyan-400 transition-colors resize-none`}
            placeholder="Cole aqui o texto que você deseja verificar...

Exemplo: Uma mensagem de WhatsApp, email suspeito, artigo, etc.

Dica: Textos mais longos (2+ parágrafos) geram resultados mais precisos."
          />
          {text.length > 0 && (
            <button
              data-testid="clear-text-btn"
              onClick={() => { setText(""); setResult(null); setError(""); }}
              className="absolute top-3 right-3 p-2 rounded-lg bg-zinc-700/80 hover:bg-zinc-600 transition-colors text-zinc-400 hover:text-white"
              title="Limpar texto"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center justify-between">
          <span className={`text-sm font-medium ${text.length > MAX_CHARS ? 'text-red-400' : 'text-zinc-300'}`}>
            {text.length}/{MAX_CHARS} caracteres {text.length < 50 && text.length > 0 && "(mínimo 50)"}
            {text.length > MAX_CHARS && " (excedido!)"}
          </span>
          <button
            data-testid="scan-text-btn"
            onClick={handleScan}
            disabled={loading || text.length < 50 || text.length > MAX_CHARS}
            className="btn-primary flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 spinner" />
                Analisando...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Verificar Texto
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && <ResultCard result={result} onClose={() => setResult(null)} />}
    </div>
  );
};

// Image Scanner
const ImageScanner = ({ checkScam }) => {
  const { token, refreshCredits } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  
  const MAX_SIZE_MB = 3;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const handleFileChange = (selectedFile) => {
    if (selectedFile) {
      const validTypes = ["image/jpeg", "image/png", "image/webp"];
      if (!validTypes.includes(selectedFile.type)) {
        setError("Formato não suportado. Use JPEG, PNG ou WebP.");
        return;
      }
      if (selectedFile.size > MAX_SIZE_BYTES) {
        setError(`Imagem muito grande. Máximo ${MAX_SIZE_MB}MB.`);
        return;
      }
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setError("");
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleScan = async () => {
    if (!file) return;
    setError("");
    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("check_scam", checkScam);

      const headers = {
        "Content-Type": "multipart/form-data",
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const res = await axios.post(`${API}/detect/image`, formData, { headers });
      setResult(res.data);
      refreshCredits(); // Atualizar créditos automaticamente
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao analisar imagem");
    } finally {
      setLoading(false);
    }
  };

  const clearImage = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError("");
  };

  return (
    <div className="space-y-6">
      <div className="scanner-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Image className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Verificador de Imagem</h2>
            <p className="text-sm text-zinc-500">Envie uma imagem para análise (máx. {MAX_SIZE_MB}MB)</p>
          </div>
        </div>

        {!preview ? (
          <div
            data-testid="image-upload-zone"
            className={`upload-zone p-8 text-center ${dragOver ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <input
              id="fileInput"
              data-testid="image-file-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => handleFileChange(e.target.files[0])}
              className="hidden"
            />
            <Upload className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-300 mb-2">Arraste uma imagem ou clique para selecionar</p>
            <p className="text-sm text-zinc-500">Formatos: JPEG, PNG, WebP (máx. {MAX_SIZE_MB}MB)</p>
          </div>
        ) : (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-full max-h-64 object-contain rounded-lg bg-zinc-900"
            />
            <button
              data-testid="clear-image-btn"
              onClick={clearImage}
              className="absolute top-2 right-2 p-2 rounded-full bg-zinc-900/80 hover:bg-zinc-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {preview && (
          <div className="mt-4 flex justify-end">
            <button
              data-testid="scan-image-btn"
              onClick={handleScan}
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 spinner" />
                  Analisando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  Verificar Imagem
                </>
              )}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && <ResultCard result={result} onClose={() => setResult(null)} />}
    </div>
  );
};

// Audio Scanner
const AudioScanner = ({ checkScam }) => {
  const { token, refreshCredits } = useAuth();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  
  const MAX_SIZE_MB = 5;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

  const handleFileChange = (selectedFile) => {
    if (selectedFile) {
      const validTypes = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/ogg", "audio/flac", "audio/x-wav"];
      const validExtensions = ['.mp3', '.wav', '.ogg', '.flac', '.m4a'];
      const hasValidExt = validExtensions.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
      
      if (!validTypes.includes(selectedFile.type) && !hasValidExt) {
        setError("Formato não suportado. Use MP3, WAV, OGG ou FLAC.");
        return;
      }
      if (selectedFile.size > MAX_SIZE_BYTES) {
        setError(`Áudio muito grande. Máximo ${MAX_SIZE_MB}MB.`);
        return;
      }
      setFile(selectedFile);
      setError("");
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleScan = async () => {
    if (!file) return;
    setError("");
    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("check_scam", checkScam);

      const headers = {
        "Content-Type": "multipart/form-data",
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const res = await axios.post(`${API}/detect/audio`, formData, { headers });
      setResult(res.data);
      refreshCredits(); // Atualizar créditos automaticamente
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao analisar áudio");
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    setError("");
  };

  return (
    <div className="space-y-6">
      <div className="scanner-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Mic className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Verificador de Áudio</h2>
            <p className="text-sm text-zinc-500">Detecte vozes clonadas ou sintéticas (máx. {MAX_SIZE_MB}MB)</p>
          </div>
        </div>

        {!file ? (
          <div
            data-testid="audio-upload-zone"
            className={`upload-zone p-8 text-center ${dragOver ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("audioInput").click()}
          >
            <input
              id="audioInput"
              data-testid="audio-file-input"
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/flac,.mp3,.wav,.ogg,.flac,.m4a"
              onChange={(e) => handleFileChange(e.target.files[0])}
              className="hidden"
            />
            <Mic className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-300 mb-2">Arraste um áudio ou clique para selecionar</p>
            <p className="text-sm text-zinc-500">Formatos: MP3, WAV, OGG, FLAC (máx. {MAX_SIZE_MB}MB)</p>
          </div>
        ) : (
          <div className="relative p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Mic className="w-6 h-6 text-emerald-400" />
              </div>
              <div className="flex-1">
                <p className="text-zinc-200 font-medium truncate">{file.name}</p>
                <p className="text-sm text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button
                data-testid="clear-audio-btn"
                onClick={clearFile}
                className="p-2 rounded-lg bg-zinc-700/80 hover:bg-zinc-600 transition-colors text-zinc-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {file && (
          <div className="mt-4 flex justify-end">
            <button
              data-testid="scan-audio-btn"
              onClick={handleScan}
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 spinner" />
                  Analisando...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4" />
                  Verificar Áudio
                </>
              )}
            </button>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && <ResultCard result={result} onClose={() => setResult(null)} />}
    </div>
  );
};

// Video Scanner
const VideoScanner = ({ checkScam }) => {
  const { token, refreshCredits } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (selectedFile) => {
    if (selectedFile) {
      const validTypes = ["video/mp4", "video/mpeg", "video/quicktime", "video/x-msvideo", "video/webm"];
      const validExtensions = ['.mp4', '.avi', '.mov', '.webm', '.mkv'];
      const hasValidExt = validExtensions.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
      
      if (!validTypes.includes(selectedFile.type) && !hasValidExt) {
        setError("Formato não suportado. Use MP4, AVI, MOV ou WebM.");
        return;
      }
      
      // Check file size (max 50MB)
      if (selectedFile.size > 50 * 1024 * 1024) {
        setError("Arquivo muito grande. Máximo 50MB.");
        return;
      }
      
      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
      setError("");
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileChange(droppedFile);
    }
  };

  const handleScan = async () => {
    if (!file) return;
    setError("");
    setLoading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("check_scam", checkScam);

      const headers = {
        "Content-Type": "multipart/form-data",
        ...(token && { Authorization: `Bearer ${token}` })
      };

      const res = await axios.post(`${API}/detect/video`, formData, { headers, timeout: 120000 });
      setResult(res.data);
      refreshCredits(); // Atualizar créditos automaticamente
    } catch (err) {
      setError(err.response?.data?.detail || "Erro ao analisar vídeo");
    } finally {
      setLoading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError("");
  };

  return (
    <div className="space-y-6">
      <div className="scanner-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-rose-500/20 flex items-center justify-center">
            <Video className="w-5 h-5 text-rose-400" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Verificador de Vídeo</h2>
            <p className="text-sm text-zinc-500">Detecte deepfakes e vídeos gerados por IA</p>
          </div>
        </div>

        {/* Info banner */}
        <div className="mb-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
          <p className="text-xs text-zinc-400">
            <strong className="text-zinc-300">Análise completa:</strong> Extraímos frames estratégicos + áudio para uma detecção mais precisa. Pode levar 15-30 segundos.
          </p>
        </div>

        {!file ? (
          <div
            data-testid="video-upload-zone"
            className={`upload-zone p-8 text-center ${dragOver ? 'drag-over' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => document.getElementById("videoInput").click()}
          >
            <input
              id="videoInput"
              data-testid="video-file-input"
              type="file"
              accept="video/mp4,video/mpeg,video/quicktime,video/x-msvideo,video/webm,.mp4,.avi,.mov,.webm,.mkv"
              onChange={(e) => handleFileChange(e.target.files[0])}
              className="hidden"
            />
            <Video className="w-12 h-12 text-zinc-500 mx-auto mb-4" />
            <p className="text-zinc-300 mb-2">Arraste um vídeo ou clique para selecionar</p>
            <p className="text-sm text-zinc-500">Formatos: MP4, AVI, MOV, WebM (máx. 50MB)</p>
          </div>
        ) : (
          <div className="relative">
            <video
              src={preview}
              className="w-full max-h-64 object-contain rounded-lg bg-zinc-900"
              controls
            />
            <button
              data-testid="clear-video-btn"
              onClick={clearFile}
              className="absolute top-2 right-2 p-2 rounded-full bg-zinc-900/80 hover:bg-zinc-800 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {file && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-zinc-400">{file.name}</span>
              <span className="text-sm text-zinc-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
            </div>
            <div className="flex justify-end">
              <button
                data-testid="scan-video-btn"
                onClick={handleScan}
                disabled={loading}
                className="btn-primary flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 spinner" />
                    Analisando vídeo...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Verificar Vídeo
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {result && <VideoResultCard result={result} onClose={() => setResult(null)} />}
    </div>
  );
};

// Video Result Card
const VideoResultCard = ({ result, onClose }) => {
  const isAI = result.is_ai_generated;
  
  return (
    <div className={`result-card ${isAI ? 'ai' : 'human'} fade-in`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isAI ? 'bg-red-500/20' : 'bg-green-500/20'}`}>
            {isAI ? (
              <AlertTriangle className="w-6 h-6 text-red-500" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green-500" />
            )}
          </div>
          <div>
            <h3 className={`text-lg font-bold ${isAI ? 'text-red-400' : 'text-green-400'}`}>
              {isAI ? 'Possível Deepfake/IA' : 'Provavelmente Real'}
            </h3>
            <p className="text-sm text-zinc-400">
              Confiança: {result.confidence.toFixed(0)}% • {result.frames_analyzed} frames analisados
            </p>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-zinc-800/50">
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        )}
      </div>

      <div className="mb-4">
        <div className="confidence-bar">
          <div
            className={`confidence-fill ${isAI ? 'ai' : 'human'}`}
            style={{ width: `${result.confidence}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="p-4 rounded-lg bg-zinc-900/50">
          <h4 className="text-sm font-semibold text-zinc-300 mb-2">Análise do Vídeo:</h4>
          <p className="text-sm text-zinc-400 leading-relaxed">{result.analysis}</p>
        </div>

        {result.audio_analysis && (
          <div className="p-4 rounded-lg bg-zinc-900/50">
            <h4 className="text-sm font-semibold text-zinc-300 mb-2">Análise do Áudio:</h4>
            <p className="text-sm text-zinc-400 leading-relaxed">{result.audio_analysis}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Link Scanner Component
const LinkScanner = ({ checkScam }) => {
  const { token, refreshCredits } = useAuth();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const COST = 2;

  const handleScan = async () => {
    if (!url.trim()) return;
    
    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/detect/link`, 
        { url: url.trim(), check_scam: checkScam },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(res.data);
      refreshCredits();
    } catch (error) {
      alert(error.response?.data?.detail || "Erro ao verificar link");
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'SAFE': return 'text-emerald-400';
      case 'LOW': return 'text-yellow-400';
      case 'MEDIUM': return 'text-orange-400';
      case 'HIGH': return 'text-red-400';
      case 'CRITICAL': return 'text-red-600';
      default: return 'text-zinc-400';
    }
  };

  const getRiskBg = (level) => {
    switch (level) {
      case 'SAFE': return 'bg-emerald-500/10 border-emerald-500/30';
      case 'LOW': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'MEDIUM': return 'bg-orange-500/10 border-orange-500/30';
      case 'HIGH': return 'bg-red-500/10 border-red-500/30';
      case 'CRITICAL': return 'bg-red-600/20 border-red-600/50';
      default: return 'bg-zinc-500/10 border-zinc-500/30';
    }
  };

  const getRiskLabel = (level) => {
    switch (level) {
      case 'SAFE': return 'Seguro';
      case 'LOW': return 'Risco Baixo';
      case 'MEDIUM': return 'Risco Médio';
      case 'HIGH': return 'Risco Alto';
      case 'CRITICAL': return 'Perigo Crítico';
      default: return 'Desconhecido';
    }
  };

  return (
    <div className="card-dark fade-in">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-blue-500/20">
          <Link2 className="w-5 h-5 text-blue-400" />
        </div>
        <div>
          <h3 className="font-semibold text-white">Verificador de Links</h3>
          <p className="text-sm text-zinc-400">Verifique se um link é seguro ou malicioso ({COST} créditos)</p>
        </div>
      </div>

      {/* URL Input */}
      <div className="mb-4">
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            data-testid="link-input"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-lg bg-zinc-900 border-2 border-blue-500/50 text-white placeholder-zinc-300 focus:outline-none focus:border-blue-400 transition-colors"
            placeholder="Cole aqui o link que deseja verificar (ex: www.exemplo.com)"
            onKeyDown={(e) => e.key === 'Enter' && handleScan()}
          />
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Dica: Cole links de emails suspeitos, SMS, WhatsApp ou sites que você não conhece
        </p>
      </div>

      {/* Scan Button */}
      <button
        data-testid="scan-link-btn"
        onClick={handleScan}
        disabled={loading || !url.trim()}
        className="w-full py-3 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 spinner" />
            Analisando...
          </>
        ) : (
          <>
            <Shield className="w-5 h-5" />
            Verificar Link
          </>
        )}
      </button>

      {/* Result */}
      {result && (
        <div className={`mt-6 p-4 rounded-xl border ${getRiskBg(result.risk_level)}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {result.is_safe ? (
                <CheckCircle className={`w-8 h-8 ${getRiskColor(result.risk_level)}`} />
              ) : (
                <ShieldAlert className={`w-8 h-8 ${getRiskColor(result.risk_level)}`} />
              )}
              <div>
                <h4 className={`font-bold text-lg ${getRiskColor(result.risk_level)}`}>
                  {getRiskLabel(result.risk_level)}
                </h4>
                <p className="text-sm text-zinc-400 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />
                  {result.url.length > 50 ? result.url.substring(0, 50) + '...' : result.url}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white">{result.security_score}</p>
              <p className="text-xs text-zinc-500">Score</p>
            </div>
          </div>

          {/* Category */}
          {result.category && (
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs text-zinc-500">Categoria:</span>
              <span className="px-2 py-1 rounded bg-zinc-800 text-xs text-zinc-300">
                {result.category.replace('PROD_', '').replace(/_/g, ' ')}
              </span>
            </div>
          )}

          {/* Recommendation */}
          <div className="p-3 rounded-lg bg-zinc-900/50 mb-3">
            <p className="text-sm text-zinc-300">{result.recommendation}</p>
          </div>

          {/* Threats Found */}
          {result.threats_found && result.threats_found.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-zinc-500 mb-2">Alertas encontrados:</p>
              <div className="space-y-1">
                {result.threats_found.map((threat, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-yellow-400">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{threat}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Scam Analysis */}
          {result.scam_analysis && (
            <div className="mt-4 p-3 rounded-lg bg-zinc-900/50 border border-orange-500/30">
              <div className="flex items-center gap-2 mb-2">
                <ShieldAlert className="w-4 h-4 text-orange-400" />
                <span className="text-sm font-semibold text-orange-400">Análise de Golpe (IA)</span>
              </div>
              <p className="text-sm text-zinc-300">{result.scam_analysis.recommendation}</p>
              {result.scam_analysis.patterns_found?.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-zinc-500 mb-1">Padrões detectados:</p>
                  <div className="flex flex-wrap gap-1">
                    {result.scam_analysis.patterns_found.map((p, i) => (
                      <span key={i} className="px-2 py-1 rounded bg-orange-500/20 text-xs text-orange-300">{p}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Credits Page
const CreditsPage = ({ onBack }) => {
  const { token, credits, refreshCredits } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [oasisBalance, setOasisBalance] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('balance'); // 'balance' ou 'pix'
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [checkingDeposits, setCheckingDeposits] = useState(false);
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);

  const fetchPendingDeposits = async () => {
    try {
      const res = await axios.get(`${API}/oasis/my-deposits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingDeposits(res.data.deposits || []);
    } catch (err) {
      console.error("Error fetching deposits:", err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [packagesRes, balanceRes] = await Promise.all([
          axios.get(`${API}/packages`),
          axios.get(`${API}/oasis/balance`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setPackages(packagesRes.data);
        setOasisBalance(balanceRes.data);
        await fetchPendingDeposits();
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Auto-check pending deposits
  useEffect(() => {
    if (pendingDeposits.length > 0) {
      const interval = setInterval(() => {
        checkAllDeposits();
      }, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [pendingDeposits]);

  const checkAllDeposits = async () => {
    setCheckingDeposits(true);
    let creditsAdded = 0;
    
    for (const deposit of pendingDeposits.filter(d => d.status === 'pending')) {
      try {
        const res = await axios.get(`${API}/oasis/deposit/${deposit.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.status === 'approved') {
          creditsAdded += res.data.credits_added || 0;
        }
      } catch (err) {
        console.error("Error checking deposit:", err);
      }
    }
    
    if (creditsAdded > 0) {
      refreshCredits();
      alert(`${creditsAdded} créditos adicionados!`);
      await fetchPendingDeposits();
    }
    
    setCheckingDeposits(false);
  };

  const handlePurchaseWithBalance = async (packageId) => {
    setPurchasing(packageId);
    try {
      const res = await axios.post(`${API}/oasis/purchase`, {
        package_id: packageId,
        payment_method: 'balance'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        refreshCredits();
        setOasisBalance(prev => ({
          ...prev,
          brl_balance: res.data.new_brl_balance
        }));
        alert(res.data.message);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Erro ao processar compra";
      alert(errorMsg);
    } finally {
      setPurchasing(null);
    }
  };

  const handlePurchaseWithPix = async (packageId) => {
    const pkg = packages.find(p => p.id === packageId);
    setPurchasing(packageId);
    try {
      const res = await axios.post(`${API}/oasis/deposit`, {
        package_id: packageId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        setPixData({
          depositId: res.data.deposit_id,
          pixKey: res.data.pix_key,
          amount: res.data.amount,
          credits: res.data.credits,
          packageName: pkg?.name || packageId
        });
        setShowPixModal(true);
        await fetchPendingDeposits();
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Erro ao criar depósito";
      alert(errorMsg);
    } finally {
      setPurchasing(null);
    }
  };

  const handlePurchase = async (packageId) => {
    if (paymentMethod === 'balance') {
      await handlePurchaseWithBalance(packageId);
    } else {
      await handlePurchaseWithPix(packageId);
    }
  };

  const copyToClipboard = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Copiado!');
    } catch (err) {
      alert('Copiado: ' + text);
    }
  };

  // Generate QR Code URL using a free API
  const getQrCodeUrl = (pixKey, amount) => {
    const pixPayload = `${pixKey}`;
    return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pixPayload)}`;
  };

  return (
    <div className="fade-in">
      <button
        data-testid="back-btn"
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 mb-4">
          <Coins className="w-8 h-8 text-amber-400" />
          <div className="text-left">
            <p className="text-sm text-amber-400/80">Seus créditos</p>
            <p className="text-3xl font-bold text-amber-300">{credits}</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold mb-2">Comprar Créditos</h2>
        <p className="text-zinc-400">Escolha o pacote ideal para você</p>
      </div>

      {/* OASIS Balance Card */}
      {oasisBalance && (
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-cyan-400">Saldo OASIS</p>
                <p className="text-xl font-bold text-white">R$ {oasisBalance.brl_balance?.toFixed(2).replace('.', ',') || '0,00'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-400">
              <span>USDT: ${oasisBalance.usdt_balance?.toFixed(2) || '0.00'}</span>
            </div>
          </div>
          
          {/* Payment Method Selector */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setPaymentMethod('balance')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                paymentMethod === 'balance'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              Usar Saldo BRL
            </button>
            <button
              onClick={() => setPaymentMethod('pix')}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                paymentMethod === 'pix'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              Pagar via PIX
            </button>
          </div>
        </div>
      )}

      {/* Pending Deposits Alert */}
      {pendingDeposits.filter(d => d.status === 'pending').length > 0 && (
        <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <div>
                <p className="font-semibold text-amber-300">
                  {pendingDeposits.filter(d => d.status === 'pending').length} Depósito(s) Pendente(s)
                </p>
                <p className="text-sm text-zinc-400">Aguardando confirmação do PIX</p>
              </div>
            </div>
            <button
              onClick={checkAllDeposits}
              disabled={checkingDeposits}
              className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium flex items-center gap-2"
            >
              {checkingDeposits ? <Loader2 className="w-4 h-4 spinner" /> : <CheckCircle className="w-4 h-4" />}
              Verificar
            </button>
          </div>
        </div>
      )}

      {/* Credit costs info */}
      <div className="mb-8 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3">Custo por análise:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50">
            <FileText className="w-5 h-5 text-cyan-400" />
            <div>
              <span className="text-sm text-zinc-300">Texto: <strong>2 créditos</strong></span>
              <p className="text-xs text-amber-400">+1 crédito se &gt;2000 caracteres</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50">
            <Image className="w-5 h-5 text-purple-400" />
            <div>
              <span className="text-sm text-zinc-300">Imagem: <strong>4 créditos</strong></span>
              <p className="text-xs text-amber-400">+1 crédito se &gt;2MB</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50">
            <Mic className="w-5 h-5 text-emerald-400" />
            <div>
              <span className="text-sm text-zinc-300">Áudio: <strong>6 créditos</strong></span>
              <p className="text-xs text-amber-400">+2 créditos se &gt;3MB</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50">
            <Video className="w-5 h-5 text-rose-400" />
            <div>
              <span className="text-sm text-zinc-300">Vídeo: <strong>20 créditos</strong></span>
              <p className="text-xs text-amber-400">+5 créditos se &gt;30MB</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 md:col-span-2">
            <Link2 className="w-5 h-5 text-blue-400" />
            <div>
              <span className="text-sm text-zinc-300">Link/URL: <strong>2 créditos</strong></span>
              <p className="text-xs text-zinc-500">Verificação via Sophos Intelix</p>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 spinner text-cyan-500" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {packages.map((pkg) => {
            const canAfford = oasisBalance?.brl_balance >= pkg.price;
            return (
              <div
                key={pkg.id}
                className={`relative p-6 rounded-xl border transition-all hover:scale-105 ${
                  pkg.id === 'pro' 
                    ? 'bg-gradient-to-b from-cyan-500/10 to-cyan-500/5 border-cyan-500/50' 
                    : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                }`}
              >
                {pkg.id === 'pro' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-cyan-500 text-xs font-bold text-white">
                    POPULAR
                  </div>
                )}
                
                <div className="text-center mb-4">
                  <Package className={`w-8 h-8 mx-auto mb-2 ${pkg.id === 'pro' ? 'text-cyan-400' : 'text-zinc-500'}`} />
                  <h3 className="text-lg font-bold">{pkg.name}</h3>
                  <p className="text-sm text-zinc-500">{pkg.description}</p>
                </div>

                <div className="text-center mb-4">
                  <span className="text-3xl font-bold">R$ {pkg.price.toFixed(2).replace('.', ',')}</span>
                </div>

                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing === pkg.id || (paymentMethod === 'balance' && !canAfford)}
                  className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all ${
                    paymentMethod === 'balance' && !canAfford
                      ? 'bg-zinc-700 text-zinc-500 cursor-not-allowed'
                      : pkg.id === 'pro'
                        ? 'bg-cyan-500 hover:bg-cyan-600 text-white'
                        : 'bg-zinc-700 hover:bg-zinc-600 text-white'
                  }`}
                >
                  {purchasing === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 spinner" />
                      Processando...
                    </>
                  ) : paymentMethod === 'balance' && !canAfford ? (
                    <>Saldo insuficiente</>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4" />
                      {paymentMethod === 'pix' ? 'Pagar via PIX' : 'Comprar'}
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 p-4 rounded-xl bg-zinc-800/30 border border-zinc-700">
        <div className="flex items-center gap-3 mb-2">
          <img src="/oasis_logo.png" alt="OASIS" className="w-6 h-6 rounded" />
          <span className="font-semibold text-white">Transações via OASIS Exchange</span>
        </div>
        <p className="text-sm text-zinc-400">
          {paymentMethod === 'balance' 
            ? 'Sua conta na OASIS foi criada automaticamente e de forma gratuita junto com seu cadastro na WISE EAGLE. Todas as transações de compra de créditos e recompensas são processadas pela OASIS. De lá, você pode fazer retiradas ou transferir para qualquer outro endereço de criptomoedas.'
            : 'Faça um PIX para a chave indicada e aguarde a aprovação. Seus créditos serão adicionados automaticamente. Todas as transações são processadas com segurança pela exchange OASIS.'}
        </p>
        <a 
          href="https://crypto-hub-rebuild.emergent.host" 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-2 text-cyan-400 hover:text-cyan-300 text-sm"
        >
          Acessar OASIS Exchange <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* PIX Modal */}
      {showPixModal && pixData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl p-6 max-w-md w-full border border-zinc-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">Pagamento PIX</h3>
              <button 
                onClick={() => setShowPixModal(false)}
                className="p-1 rounded-lg hover:bg-zinc-800"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-zinc-400 mb-2">Pacote: <span className="text-white font-semibold">{pixData.packageName}</span></p>
              <p className="text-3xl font-bold text-emerald-400">R$ {pixData.amount.toFixed(2).replace('.', ',')}</p>
              <p className="text-sm text-zinc-500 mt-1">{pixData.credits} créditos</p>
            </div>

            {/* QR Code */}
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white rounded-xl">
                <img 
                  src={getQrCodeUrl(pixData.pixKey, pixData.amount)} 
                  alt="QR Code PIX" 
                  className="w-48 h-48"
                />
              </div>
            </div>

            {/* PIX Key */}
            <div className="mb-6">
              <label className="text-sm text-zinc-400 mb-2 block">Chave PIX (Email)</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={pixData.pixKey} 
                  readOnly 
                  className="flex-1 p-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white text-sm"
                />
                <button 
                  onClick={() => copyToClipboard(pixData.pixKey)}
                  className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-300">
                <strong>Importante:</strong> Após fazer o PIX, aguarde a confirmação. 
                Clique em "Verificar Depósitos" para atualizar o status.
              </p>
            </div>

            <button
              onClick={() => {
                setShowPixModal(false);
                checkAllDeposits();
              }}
              className="w-full py-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold"
            >
              Já fiz o PIX - Verificar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Antivirus Scanner Component
const AntivirusScanner = () => {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = (e) => {
    const selected = e.target.files[0];
    if (selected) {
      if (selected.size > 32 * 1024 * 1024) {
        setError('Arquivo muito grande. Máximo: 32MB');
        return;
      }
      setFile(selected);
      setResult(null);
      setError(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) {
      if (dropped.size > 32 * 1024 * 1024) {
        setError('Arquivo muito grande. Máximo: 32MB');
        return;
      }
      setFile(dropped);
      setResult(null);
      setError(null);
    }
  };

  const handleScan = async () => {
    if (!file) return;
    setScanning(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/scan/file`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      
      const data = res.data;
      
      if (data.status === 'analyzing' && data.analysis_id) {
        // Poll for results
        setResult({ ...data, message: 'Arquivo enviado! Analisando com 70+ antivírus...' });
        
        for (let i = 0; i < 24; i++) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            const pollRes = await axios.get(`${API}/scan/file/${data.analysis_id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
              timeout: 15000
            });
            if (pollRes.data.status === 'completed') {
              setResult({ ...pollRes.data, filename: data.filename, file_size: data.file_size, credits_used: data.credits_used });
              setScanning(false);
              return;
            }
          } catch (pollErr) {
            // Continue polling
          }
        }
        setResult({ ...data, message: 'Análise ainda em andamento. Tente verificar o histórico em alguns minutos.' });
      } else {
        setResult(data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao escanear arquivo');
    } finally {
      setScanning(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critico': return 'text-red-500';
      case 'alto': return 'text-orange-500';
      case 'medio': return 'text-amber-500';
      case 'baixo': return 'text-yellow-400';
      default: return 'text-emerald-400';
    }
  };

  const getRiskBg = (level) => {
    switch (level) {
      case 'critico': return 'bg-red-500/10 border-red-500/30';
      case 'alto': return 'bg-orange-500/10 border-orange-500/30';
      case 'medio': return 'bg-amber-500/10 border-amber-500/30';
      case 'baixo': return 'bg-yellow-500/10 border-yellow-500/30';
      default: return 'bg-emerald-500/10 border-emerald-500/30';
    }
  };

  return (
    <div className="fade-in" data-testid="antivirus-scanner">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium mb-2">
          <Bug className="w-3 h-3" />
          70+ Antivírus
        </div>
        <p className="text-sm text-zinc-400">Escaneie arquivos suspeitos contra mais de 70 mecanismos antivírus</p>
        <p className="text-xs text-zinc-500 mt-1">Custo: 3 créditos | Máximo: 32MB</p>
      </div>

      {/* Simple explanation for everyone */}
      <div className="mb-6 p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20">
        <h3 className="text-sm font-semibold text-cyan-400 mb-2 flex items-center gap-2">
          <HelpCircle className="w-4 h-4" />
          Como funciona? (explicação simples)
        </h3>
        <p className="text-sm text-zinc-300 leading-relaxed">
          Sabe quando você recebe um arquivo pelo WhatsApp, e-mail ou baixa da internet e fica na dúvida se é seguro? 
          Pode ser uma foto, um documento, um programa ou qualquer outro arquivo.
        </p>
        <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
          <strong className="text-white">Como enviar o arquivo:</strong> Clique no quadrado pontilhado abaixo (ou arraste o arquivo até ele). 
          Vai abrir uma janela para você escolher o arquivo do seu celular ou computador — igual quando você envia uma foto no WhatsApp. 
          Depois é só apertar o botão <strong className="text-cyan-400">"Escanear Arquivo"</strong> e aguardar o resultado.
        </p>
        <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
          É como levar o arquivo no "médico" — <strong className="text-cyan-400">mais de 70 antivírus famosos</strong> (Avast, 
          Kaspersky, Norton e outros) analisam o arquivo ao mesmo tempo e te dizem se é seguro ou perigoso. 
          Tudo isso em poucos segundos!
        </p>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          dragOver ? 'border-cyan-500 bg-cyan-500/5' : 'border-zinc-700 hover:border-zinc-500'
        }`}
        onClick={() => document.getElementById('antivirus-file-input').click()}
      >
        <input
          id="antivirus-file-input"
          type="file"
          className="hidden"
          onChange={handleFileSelect}
        />
        {file ? (
          <div className="flex items-center justify-center gap-3">
            <FileSearch className="w-8 h-8 text-cyan-400" />
            <div className="text-left">
              <p className="text-white font-medium">{file.name}</p>
              <p className="text-xs text-zinc-500">{formatFileSize(file.size)}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); setFile(null); setResult(null); }} className="ml-2 text-zinc-500 hover:text-red-400">
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-10 h-10 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">Arraste um arquivo ou clique para selecionar</p>
            <p className="text-zinc-600 text-xs mt-1">Qualquer tipo de arquivo até 32MB</p>
          </>
        )}
      </div>

      {/* Scan Button */}
      <button
        onClick={handleScan}
        disabled={!file || scanning}
        className="w-full mt-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="antivirus-scan-btn"
      >
        {scanning ? (
          <>
            <Loader2 className="w-5 h-5 spinner" />
            Escaneando... (pode levar até 60s)
          </>
        ) : (
          <>
            <Shield className="w-5 h-5" />
            Escanear Arquivo (3 créditos)
          </>
        )}
      </button>

      {error && (
        <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Results */}
      {result && result.status === 'completed' && (
        <div className="mt-6 space-y-4 fade-in">
          {/* Main Result */}
          <div className={`p-6 rounded-xl border ${getRiskBg(result.risk_level)}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {result.is_safe ? (
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                ) : (
                  <AlertOctagon className="w-8 h-8 text-red-400" />
                )}
                <div>
                  <h3 className="text-lg font-bold text-white">{result.is_safe ? 'Arquivo Seguro' : 'Ameaça Detectada!'}</h3>
                  <p className={`text-sm font-medium ${getRiskColor(result.risk_level)}`}>
                    Risco: {result.risk_level.charAt(0).toUpperCase() + result.risk_level.slice(1)}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-white">{result.threats_detected}/{result.total_engines}</p>
                <p className="text-xs text-zinc-400">detecções</p>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-zinc-800 mb-3">
              {result.malicious > 0 && (
                <div className="bg-red-500 rounded-full" style={{width: `${(result.malicious / result.total_engines) * 100}%`}} />
              )}
              {result.suspicious > 0 && (
                <div className="bg-amber-500 rounded-full" style={{width: `${(result.suspicious / result.total_engines) * 100}%`}} />
              )}
              {result.undetected > 0 && (
                <div className="bg-zinc-600 rounded-full" style={{width: `${(result.undetected / result.total_engines) * 100}%`}} />
              )}
              {result.harmless > 0 && (
                <div className="bg-emerald-500 rounded-full" style={{width: `${(result.harmless / result.total_engines) * 100}%`}} />
              )}
            </div>

            <div className="grid grid-cols-4 gap-2 text-center text-xs">
              <div><span className="text-red-400 font-bold">{result.malicious}</span><br/><span className="text-zinc-500">Malicioso</span></div>
              <div><span className="text-amber-400 font-bold">{result.suspicious}</span><br/><span className="text-zinc-500">Suspeito</span></div>
              <div><span className="text-zinc-400 font-bold">{result.undetected}</span><br/><span className="text-zinc-500">Não detectado</span></div>
              <div><span className="text-emerald-400 font-bold">{result.harmless}</span><br/><span className="text-zinc-500">Seguro</span></div>
            </div>
          </div>

          {/* Detections List */}
          {result.detections && result.detections.length > 0 && (
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                Antivírus que detectaram ameaças ({result.detections.length})
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {result.detections.map((d, i) => (
                  <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-zinc-900/50 text-sm">
                    <span className="text-zinc-300 font-medium">{d.engine}</span>
                    <span className={`text-xs px-2 py-1 rounded-full ${d.category === 'malicious' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {d.result}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Info */}
          <div className="p-3 rounded-lg bg-zinc-800/30 border border-zinc-700/50 text-xs text-zinc-500">
            Arquivo: {result.filename} ({formatFileSize(result.file_size)}) | Créditos usados: {result.credits_used}
          </div>
        </div>
      )}

      {result && result.status === 'analyzing' && (
        <div className="mt-6 p-6 rounded-xl bg-amber-500/10 border border-amber-500/30 text-center">
          <Loader2 className="w-8 h-8 text-amber-400 mx-auto mb-3 spinner" />
          <p className="text-amber-300 font-medium">Análise em andamento</p>
          <p className="text-sm text-zinc-400 mt-1">{result.message}</p>
        </div>
      )}
    </div>
  );
};

// Referral Page
const ReferralPage = ({ onBack }) => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [network, setNetwork] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [copied, setCopied] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const referralLink = stats?.referral_code 
    ? `${window.location.origin}?ref=${stats.referral_code}` 
    : '';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, networkRes, historyRes] = await Promise.all([
          axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/referral/network`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/referral/history`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setStats(statsRes.data);
        setNetwork(networkRes.data.network || []);
        setHistory(historyRes.data.history || []);
      } catch (err) {
        console.error("Error fetching referral data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const copyToClipboard = (text) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    } catch (err) {
      console.warn('Copy failed, using prompt fallback');
      window.prompt('Copie o link:', text);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (isNaN(amount) || amount < 100) {
      alert('Valor mínimo para saque é $100.00');
      return;
    }
    if (amount > (stats?.pending_commission_usd || 0)) {
      alert('Saldo insuficiente');
      return;
    }
    
    setWithdrawing(true);
    try {
      const res = await axios.post(`${API}/referral/withdraw`, 
        { amount_usd: amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(res.data.message);
      setWithdrawAmount('');
      // Refresh stats
      const statsRes = await axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(statsRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao solicitar saque');
    } finally {
      setWithdrawing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 spinner text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 px-6 py-4 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 mb-4">
          <Users className="w-8 h-8 text-emerald-400" />
          <div className="text-left">
            <p className="text-sm text-emerald-400/80">Programa de Indicação</p>
            <p className="text-2xl font-bold text-emerald-300">Ganhe com WISE EAGLE</p>
          </div>
        </div>
      </div>

      {/* Referral Link Card */}
      <div className="mb-6 p-5 rounded-xl bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
          <Share2 className="w-4 h-4 text-cyan-400" />
          Seu Link de Indicação
        </h3>
        <div className="flex gap-2">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="flex-1 px-4 py-2 rounded-lg bg-zinc-900/50 border border-zinc-700 text-zinc-300 text-sm"
          />
          <button
            onClick={() => copyToClipboard(referralLink)}
            className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white font-medium text-sm flex items-center gap-2 transition-colors"
          >
            {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copiado!' : 'Copiar'}
          </button>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          Código: <span className="font-mono text-cyan-400">{stats?.referral_code}</span>
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
          <Users className="w-5 h-5 text-cyan-400 mb-2" />
          <p className="text-2xl font-bold text-white">{stats?.total_referrals || 0}</p>
          <p className="text-xs text-zinc-500">Total Indicados</p>
        </div>
        <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
          <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-2xl font-bold text-emerald-400">${(stats?.total_commission_usd || 0).toFixed(2)}</p>
          <p className="text-xs text-zinc-500">Total Ganho</p>
        </div>
        <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
          <Wallet className="w-5 h-5 text-amber-400 mb-2" />
          <p className="text-2xl font-bold text-amber-400">${(stats?.pending_commission_usd || 0).toFixed(2)}</p>
          <p className="text-xs text-zinc-500">Disponível</p>
        </div>
        <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
          <DollarSign className="w-5 h-5 text-zinc-400 mb-2" />
          <p className="text-2xl font-bold text-zinc-300">${(stats?.withdrawn_commission_usd || 0).toFixed(2)}</p>
          <p className="text-xs text-zinc-500">Sacado</p>
        </div>
      </div>

      {/* Referral Levels */}
      <div className="mb-6 p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
        <h3 className="text-sm font-semibold text-zinc-300 mb-3">Seus Níveis de Indicação</h3>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
            <p className="text-xl font-bold text-cyan-400">{stats?.level_1_referrals || 0}</p>
            <p className="text-xs text-zinc-400">Nível 1 (10%)</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
            <p className="text-xl font-bold text-emerald-400">{stats?.level_2_referrals || 0}</p>
            <p className="text-xs text-zinc-400">Nível 2 (3%)</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <p className="text-xl font-bold text-amber-400">{stats?.level_3_referrals || 0}</p>
            <p className="text-xs text-zinc-400">Nível 3 (1%)</p>
          </div>
        </div>
      </div>

      {/* Withdraw Section - Always visible */}
      <div className={`mb-6 p-4 rounded-xl bg-gradient-to-r ${stats?.can_withdraw ? 'from-amber-500/10 to-orange-500/10 border border-amber-500/30' : 'from-zinc-800/50 to-zinc-700/50 border border-zinc-700'}`}>
        <h3 className="text-sm font-semibold text-amber-300 mb-3 flex items-center gap-2">
          <Wallet className="w-4 h-4" />
          Retirar Recompensa para OASIS
        </h3>
        {stats?.can_withdraw ? (
          <>
            <div className="flex gap-2">
              <input
                type="number"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                placeholder="Valor em USD"
                min="100"
                max={stats?.pending_commission_usd || 0}
                className="flex-1 px-4 py-2 rounded-lg bg-zinc-900/50 border border-zinc-700 text-white"
              />
              <button
                onClick={handleWithdraw}
                disabled={withdrawing}
                className="px-6 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
              >
                {withdrawing ? <Loader2 className="w-4 h-4 spinner" /> : <DollarSign className="w-4 h-4" />}
                Sacar
              </button>
            </div>
            <p className="mt-2 text-xs text-amber-400/70">
              Recompensa enviada em USDT para sua conta OASIS Exchange
            </p>
          </>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="number"
                disabled
                placeholder="Valor em USD"
                className="flex-1 px-4 py-2 rounded-lg bg-zinc-900/30 border border-zinc-700/50 text-zinc-500 cursor-not-allowed"
              />
              <button
                disabled
                className="px-6 py-2 rounded-lg bg-zinc-600 text-zinc-400 font-medium flex items-center gap-2 cursor-not-allowed opacity-50"
              >
                <DollarSign className="w-4 h-4" />
                Sacar
              </button>
            </div>
            <p className="mt-2 text-xs text-zinc-500">
              Valor mínimo para saque: <span className="text-amber-400">$100.00</span> — Faltam <span className="text-amber-400">${(100 - (stats?.pending_commission_usd || 0)).toFixed(2)}</span> para liberar o saque
            </p>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'overview' ? 'bg-cyan-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
        >
          Como Funciona
        </button>
        <button
          onClick={() => setActiveTab('network')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'network' ? 'bg-cyan-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
        >
          Minha Rede ({network.length})
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === 'history' ? 'bg-cyan-500 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
        >
          Histórico
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Gift className="w-5 h-5 text-cyan-400" />
              Bônus de Indicação
            </h4>
            <p className="text-sm text-zinc-400">
              Você e seu indicado ganham <span className="text-cyan-400 font-bold">5 créditos grátis</span> cada quando alguém se cadastra usando seu link!
            </p>
          </div>
          
          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" />
              Programa de Indicação (Member get Member)
            </h4>
            <p className="text-sm text-zinc-400 mb-3">
              Ganhe cripto (USDT) quando seus indicados comprarem qualquer plano de créditos:
            </p>
            <div className="space-y-2">
              <div className="flex justify-between items-center p-2 rounded bg-zinc-900/50">
                <span className="text-sm text-zinc-300">Nível 1 (indicação direta)</span>
                <span className="text-emerald-400 font-bold">10%</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-zinc-900/50">
                <span className="text-sm text-zinc-300">Nível 2 (indicado do seu indicado)</span>
                <span className="text-emerald-400 font-bold">3%</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-zinc-900/50">
                <span className="text-sm text-zinc-300">Nível 3 (terceiro nível)</span>
                <span className="text-emerald-400 font-bold">1%</span>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-400" />
              Recompensa
            </h4>
            <p className="text-sm text-zinc-400">
              As recompensas em <span className="text-amber-400 font-bold">USDT/USDC</span> podem ser retiradas via exchange OASIS. 
              Valor mínimo para saque: <span className="text-amber-400 font-bold">$100.00</span>
            </p>
          </div>
        </div>
      )}

      {activeTab === 'network' && (
        <div className="space-y-3">
          {network.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Você ainda não tem indicados</p>
              <p className="text-sm">Compartilhe seu link e comece a ganhar!</p>
            </div>
          ) : (
            network.map((member, index) => (
              <div key={index} className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${member.level === 1 ? 'bg-cyan-500/20' : member.level === 2 ? 'bg-emerald-500/20' : 'bg-amber-500/20'}`}>
                    <User className={`w-5 h-5 ${member.level === 1 ? 'text-cyan-400' : member.level === 2 ? 'text-emerald-400' : 'text-amber-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-white">{member.name}</p>
                    <p className="text-xs text-zinc-500">{member.email}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${member.level === 1 ? 'bg-cyan-500/20 text-cyan-400' : member.level === 2 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                    Nível {member.level}
                  </span>
                  {member.commission_generated_usd > 0 && (
                    <p className="text-xs text-emerald-400 mt-1">+${member.commission_generated_usd.toFixed(2)}</p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-3">
          {history.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Nenhuma transação ainda</p>
            </div>
          ) : (
            history.map((item, index) => (
              <div key={index} className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${item.type === 'referral_bonus' ? 'bg-cyan-500/20' : 'bg-emerald-500/20'}`}>
                    {item.type === 'referral_bonus' ? (
                      <Gift className="w-5 h-5 text-cyan-400" />
                    ) : (
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-white">
                      {item.type === 'referral_bonus' ? 'Bônus de Indicação' : `Recompensa Nível ${item.level}`}
                    </p>
                    <p className="text-xs text-zinc-500">De: {item.from_user_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  {item.type === 'referral_bonus' ? (
                    <p className="font-bold text-cyan-400">+{item.credits_awarded} créditos</p>
                  ) : (
                    <p className="font-bold text-emerald-400">+${item.amount_usd?.toFixed(2)}</p>
                  )}
                  <p className="text-xs text-zinc-500">{new Date(item.created_at).toLocaleDateString('pt-BR')}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Admin Page
const AdminPage = ({ onBack }) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [referrals, setReferrals] = useState(null);
  const [settings, setSettings] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState('');
  const [creditsReason, setCreditsReason] = useState('');
  const [updatingCredits, setUpdatingCredits] = useState(false);

  // Fetch dashboard stats
  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      console.error("Error fetching dashboard:", err);
    }
  };

  // Fetch users
  const fetchUsers = async (page = 1, search = '') => {
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 10, search }
      });
      setUsers(res.data.users);
      setUsersTotal(res.data.total);
      setUsersPage(res.data.page);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  // Fetch transactions
  const fetchTransactions = async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/transactions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: 10 }
      });
      setTransactions(res.data.transactions);
      setTransactionsTotal(res.data.total);
      setTransactionsPage(res.data.page);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    }
  };

  // Fetch referrals
  const fetchReferrals = async () => {
    try {
      const res = await axios.get(`${API}/admin/referrals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferrals(res.data);
    } catch (err) {
      console.error("Error fetching referrals:", err);
    }
  };

  // Fetch settings
  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchUsers(),
        fetchTransactions(),
        fetchReferrals(),
        fetchSettings()
      ]);
      setLoading(false);
    };
    loadData();
  }, [token]);

  const handleSearch = () => {
    fetchUsers(1, searchQuery);
  };

  const handleUpdateCredits = async () => {
    if (!selectedUser || !creditsToAdd) return;
    
    setUpdatingCredits(true);
    try {
      const res = await axios.post(`${API}/admin/users/${selectedUser.id}/credits`, {
        user_id: selectedUser.id,
        credits: parseInt(creditsToAdd),
        reason: creditsReason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(res.data.message);
      setSelectedUser(null);
      setCreditsToAdd('');
      setCreditsReason('');
      fetchUsers(usersPage, searchQuery);
      fetchDashboard();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao atualizar créditos");
    } finally {
      setUpdatingCredits(false);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'users', label: 'Usuários', icon: Users },
    { id: 'transactions', label: 'Transações', icon: Receipt },
    { id: 'referrals', label: 'Indicações', icon: Network },
    { id: 'settings', label: 'Configurações', icon: Settings }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 spinner text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
          <UserCog className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-bold">Painel Admin</h2>
          <p className="text-zinc-400 text-sm">Gerenciamento do WISE EAGLE</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'bg-cyan-500 text-white'
                : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30">
              <Users className="w-5 h-5 text-cyan-400 mb-2" />
              <p className="text-3xl font-bold text-white">{dashboard.stats.total_users}</p>
              <p className="text-xs text-zinc-400">Total Usuários</p>
              <p className="text-xs text-cyan-400">+{dashboard.stats.users_today} hoje</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-500/20 to-green-500/20 border border-emerald-500/30">
              <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="text-3xl font-bold text-white">R$ {dashboard.stats.total_revenue?.toFixed(2).replace('.', ',')}</p>
              <p className="text-xs text-zinc-400">Receita Total</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
              <Coins className="w-5 h-5 text-amber-400 mb-2" />
              <p className="text-3xl font-bold text-white">{dashboard.stats.total_credits}</p>
              <p className="text-xs text-zinc-400">Créditos em Circulação</p>
            </div>
            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
              <Share2 className="w-5 h-5 text-purple-400 mb-2" />
              <p className="text-3xl font-bold text-white">{dashboard.stats.total_referrals}</p>
              <p className="text-xs text-zinc-400">Indicações</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <h3 className="font-semibold mb-3">Transações</h3>
              <div className="flex justify-between items-center">
                <span className="text-zinc-400">Completadas</span>
                <span className="text-emerald-400 font-bold">{dashboard.stats.completed_transactions}</span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-zinc-400">Total</span>
                <span className="text-white font-bold">{dashboard.stats.total_transactions}</span>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <h3 className="font-semibold mb-3">Recompensas Pagas</h3>
              <p className="text-2xl font-bold text-emerald-400">${dashboard.stats.total_commissions?.toFixed(2)}</p>
              <p className="text-xs text-zinc-400">Total em USDT</p>
            </div>
          </div>

          {dashboard.recent_transactions?.length > 0 && (
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <h3 className="font-semibold mb-3">Transações Recentes</h3>
              <div className="space-y-2">
                {dashboard.recent_transactions.map((tx, i) => (
                  <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-zinc-900/50">
                    <div>
                      <span className="text-sm">{tx.package_id}</span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                        tx.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                      }`}>{tx.status}</span>
                    </div>
                    <span className="text-zinc-400">R$ {tx.amount?.toFixed(2).replace('.', ',')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nome ou email..."
              className="flex-1 p-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white placeholder-zinc-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button
              onClick={handleSearch}
              className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white"
            >
              <Search className="w-5 h-5" />
            </button>
          </div>

          <div className="rounded-xl border border-zinc-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-left p-3 text-xs text-zinc-400">Usuário</th>
                  <th className="text-left p-3 text-xs text-zinc-400">Email</th>
                  <th className="text-center p-3 text-xs text-zinc-400">Créditos</th>
                  <th className="text-center p-3 text-xs text-zinc-400">Ações</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t border-zinc-800 hover:bg-zinc-800/50">
                    <td className="p-3">
                      <span className="font-medium text-white">{user.name}</span>
                    </td>
                    <td className="p-3 text-sm text-zinc-400">{user.email}</td>
                    <td className="p-3 text-center">
                      <span className="text-amber-400 font-bold">{user.credits || 0}</span>
                    </td>
                    <td className="p-3 text-center">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="p-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white text-xs"
                      >
                        <Coins className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-zinc-400">
              {usersTotal} usuários encontrados
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => fetchUsers(usersPage - 1, searchQuery)}
                disabled={usersPage <= 1}
                className="px-3 py-1 rounded bg-zinc-700 text-white disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-zinc-400">Página {usersPage}</span>
              <button
                onClick={() => fetchUsers(usersPage + 1, searchQuery)}
                disabled={users.length < 10}
                className="px-3 py-1 rounded bg-zinc-700 text-white disabled:opacity-50"
              >
                Próximo
              </button>
            </div>
          </div>

          {/* Modal para adicionar créditos */}
          {selectedUser && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-zinc-900 rounded-xl p-6 max-w-md w-full border border-zinc-700">
                <h3 className="text-lg font-bold mb-4">Gerenciar Créditos</h3>
                <div className="mb-4">
                  <p className="text-zinc-400 text-sm">Usuário: <span className="text-white">{selectedUser.name}</span></p>
                  <p className="text-zinc-400 text-sm">Email: <span className="text-white">{selectedUser.email}</span></p>
                  <p className="text-zinc-400 text-sm">Créditos atuais: <span className="text-amber-400 font-bold">{selectedUser.credits || 0}</span></p>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Créditos (+ adicionar, - remover)</label>
                    <input
                      type="number"
                      value={creditsToAdd}
                      onChange={(e) => setCreditsToAdd(e.target.value)}
                      placeholder="Ex: 100 ou -50"
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-1 block">Motivo (opcional)</label>
                    <input
                      type="text"
                      value={creditsReason}
                      onChange={(e) => setCreditsReason(e.target.value)}
                      placeholder="Ex: Bônus especial"
                      className="w-full p-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="flex-1 py-2 rounded-lg bg-zinc-700 hover:bg-zinc-600 text-white"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleUpdateCredits}
                    disabled={!creditsToAdd || updatingCredits}
                    className="flex-1 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {updatingCredits && <Loader2 className="w-4 h-4 spinner" />}
                    Atualizar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Transactions Tab */}
      {activeTab === 'transactions' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-zinc-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-left p-3 text-xs text-zinc-400">Data</th>
                  <th className="text-left p-3 text-xs text-zinc-400">Usuário</th>
                  <th className="text-left p-3 text-xs text-zinc-400">Pacote</th>
                  <th className="text-center p-3 text-xs text-zinc-400">Valor</th>
                  <th className="text-center p-3 text-xs text-zinc-400">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, i) => (
                  <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-800/50">
                    <td className="p-3 text-sm text-zinc-400">
                      {new Date(tx.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="p-3">
                      <p className="text-sm text-white">{tx.user_name || 'N/A'}</p>
                      <p className="text-xs text-zinc-500">{tx.user_email}</p>
                    </td>
                    <td className="p-3 text-sm">{tx.package_id}</td>
                    <td className="p-3 text-center text-sm">
                      R$ {tx.amount?.toFixed(2).replace('.', ',')}
                    </td>
                    <td className="p-3 text-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        tx.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 
                        tx.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-zinc-400">
              {transactionsTotal} transações
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => fetchTransactions(transactionsPage - 1)}
                disabled={transactionsPage <= 1}
                className="px-3 py-1 rounded bg-zinc-700 text-white disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-zinc-400">Página {transactionsPage}</span>
              <button
                onClick={() => fetchTransactions(transactionsPage + 1)}
                disabled={transactions.length < 10}
                className="px-3 py-1 rounded bg-zinc-700 text-white disabled:opacity-50"
              >
                Próximo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Referrals Tab */}
      {activeTab === 'referrals' && referrals && (
        <div className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <h3 className="font-semibold mb-2">Bônus de Cadastro</h3>
              <p className="text-2xl font-bold text-cyan-400">{referrals.total_signup_bonuses}</p>
              <p className="text-xs text-zinc-400">Total de bônus dados</p>
            </div>
            <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
              <h3 className="font-semibold mb-2">Recompensas Pagas</h3>
              <p className="text-2xl font-bold text-emerald-400">{referrals.total_commission_transactions}</p>
              <p className="text-xs text-zinc-400">Transações de recompensa</p>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h3 className="font-semibold mb-4">Top Indicadores</h3>
            <div className="space-y-3">
              {referrals.top_referrers?.map((referrer, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      i === 0 ? 'bg-amber-500 text-black' :
                      i === 1 ? 'bg-zinc-400 text-black' :
                      i === 2 ? 'bg-orange-600 text-white' :
                      'bg-zinc-700 text-white'
                    }`}>{i + 1}</span>
                    <div>
                      <p className="font-medium text-white">{referrer.name}</p>
                      <p className="text-xs text-zinc-500">{referrer.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-emerald-400 font-bold">${referrer.commission_total_usd?.toFixed(2)}</p>
                    <p className="text-xs text-zinc-400">{referrer.referral_count} indicados</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && settings && (
        <div className="space-y-6">
          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h3 className="font-semibold mb-4">Pacotes de Créditos</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {Object.entries(settings.credit_packages).map(([id, pkg]) => (
                <div key={id} className="p-3 rounded-lg bg-zinc-900/50 flex justify-between items-center">
                  <div>
                    <p className="font-medium text-white">{pkg.name}</p>
                    <p className="text-sm text-zinc-400">{pkg.credits} créditos</p>
                  </div>
                  <p className="text-lg font-bold text-emerald-400">R$ {pkg.price.toFixed(2).replace('.', ',')}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h3 className="font-semibold mb-4">Taxas de Recompensa</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-center">
                <p className="text-2xl font-bold text-cyan-400">{(settings.commission_rates.level_1 * 100).toFixed(0)}%</p>
                <p className="text-xs text-zinc-400">Nível 1</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                <p className="text-2xl font-bold text-emerald-400">{(settings.commission_rates.level_2 * 100).toFixed(0)}%</p>
                <p className="text-xs text-zinc-400">Nível 2</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center">
                <p className="text-2xl font-bold text-amber-400">{(settings.commission_rates.level_3 * 100).toFixed(0)}%</p>
                <p className="text-xs text-zinc-400">Nível 3</p>
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-zinc-800/50 border border-zinc-700">
            <h3 className="font-semibold mb-4">Administradores</h3>
            <div className="space-y-2">
              {settings.admin_emails?.map((email, i) => (
                <div key={i} className="p-3 rounded-lg bg-zinc-900/50 flex items-center gap-2">
                  <UserCog className="w-4 h-4 text-cyan-400" />
                  <span className="text-white">{email}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// History Page
const HistoryPage = ({ onBack }) => {
  const { token } = useAuth();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistory(res.data);
      } catch (err) {
        console.error("Error fetching history:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [token]);

  return (
    <div className="fade-in">
      <button
        data-testid="back-btn"
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <h2 className="text-2xl font-bold mb-6">Histórico de Verificações</h2>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 spinner text-cyan-500" />
        </div>
      ) : history.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400">Nenhuma verificação realizada ainda</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {history.map((item) => (
            <div key={item.id} className="history-card" data-testid="history-item">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`badge ${
                    item.type === 'text' ? 'badge-text' : 
                    item.type === 'image' ? 'badge-image' : 
                    item.type === 'audio' ? 'badge-audio' : 'badge-video'
                  }`}>
                    {item.type === 'text' ? 'Texto' : 
                     item.type === 'image' ? 'Imagem' : 
                     item.type === 'audio' ? 'Áudio' : 'Vídeo'}
                  </span>
                  <span className={`badge ${item.is_ai_generated ? 'badge-ai' : 'badge-human'}`}>
                    {item.is_ai_generated ? 'IA' : 'Real'}
                  </span>
                </div>
                <span className="text-xs text-zinc-500">
                  {new Date(item.timestamp).toLocaleDateString('pt-BR')}
                </span>
              </div>

              <p className="text-sm text-zinc-400 mb-3 line-clamp-2">
                {item.input_preview}
              </p>

              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">
                  Confiança: {item.confidence.toFixed(0)}%
                </span>
                <div className="w-24 h-2 rounded-full bg-zinc-800">
                  <div
                    className={`h-full rounded-full ${item.is_ai_generated ? 'bg-red-500' : 'bg-green-500'}`}
                    style={{ width: `${item.confidence}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ LEGAL PAGES ============

// Privacy Policy Page
const PrivacyPolicyPage = ({ onBack }) => {
  return (
    <div className="fade-in max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="glass-panel rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Lock className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Política de Privacidade</h1>
            <p className="text-sm text-zinc-500">Última atualização: {APP_INFO.lastUpdate}</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-zinc-300">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introdução</h2>
            <p>
              O <strong>{APP_INFO.name}</strong> ("nós", "nosso" ou "aplicativo") está comprometido em proteger sua privacidade. 
              Esta Política de Privacidade explica como coletamos, usamos, armazenamos e protegemos suas informações pessoais 
              quando você utiliza nosso serviço de detecção de conteúdo gerado por Inteligência Artificial.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Dados que Coletamos</h2>
            <p>Coletamos os seguintes tipos de informações:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Dados de cadastro:</strong> nome, e-mail e senha (criptografada)</li>
              <li><strong>Conteúdo enviado para análise:</strong> textos, imagens, áudios e vídeos que você submete para verificação</li>
              <li><strong>Histórico de verificações:</strong> resultados das análises realizadas</li>
              <li><strong>Dados de pagamento:</strong> processados de forma segura pelo Stripe (não armazenamos dados de cartão)</li>
              <li><strong>Dados de uso:</strong> informações sobre como você utiliza o aplicativo</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Como Usamos seus Dados</h2>
            <p>Utilizamos suas informações para:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Fornecer o serviço de detecção de conteúdo por IA</li>
              <li>Manter seu histórico de verificações</li>
              <li>Processar pagamentos e gerenciar sua conta</li>
              <li>Melhorar nossos algoritmos de detecção</li>
              <li>Enviar comunicações importantes sobre o serviço</li>
              <li>Cumprir obrigações legais</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Compartilhamento de Dados</h2>
            <p>Seus dados podem ser compartilhados com:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Provedores de IA:</strong> OpenAI e Google (para processamento das análises)</li>
              <li><strong>Processadores de pagamento:</strong> Stripe (para transações financeiras)</li>
              <li><strong>Autoridades legais:</strong> quando exigido por lei</li>
            </ul>
            <p className="mt-3">
              <strong>Importante:</strong> Nunca vendemos seus dados pessoais para terceiros.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Armazenamento e Segurança</h2>
            <p>
              Seus dados são armazenados em servidores seguros com criptografia. 
              Implementamos medidas técnicas e organizacionais para proteger suas informações contra acesso não autorizado, 
              alteração, divulgação ou destruição.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Retenção de Dados</h2>
            <p>
              Mantemos seus dados enquanto sua conta estiver ativa. O conteúdo enviado para análise 
              (textos, imagens, áudios, vídeos) é processado e não é armazenado permanentemente após a análise, 
              exceto um resumo no seu histórico.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Seus Direitos (LGPD)</h2>
            <p>Conforme a Lei Geral de Proteção de Dados (LGPD), você tem direito a:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Acessar seus dados pessoais</li>
              <li>Corrigir dados incompletos ou desatualizados</li>
              <li>Solicitar a exclusão dos seus dados</li>
              <li>Revogar o consentimento a qualquer momento</li>
              <li>Solicitar a portabilidade dos dados</li>
              <li>Obter informações sobre o compartilhamento de dados</li>
            </ul>
            <p className="mt-3">
              Para exercer esses direitos, entre em contato: <strong>{APP_INFO.email}</strong>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Cookies e Tecnologias Similares</h2>
            <p>
              Utilizamos cookies e armazenamento local para manter sua sessão ativa e preferências. 
              Você pode gerenciar cookies nas configurações do seu navegador.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Menores de Idade</h2>
            <p>
              Nosso serviço não é direcionado a menores de 18 anos. Não coletamos intencionalmente 
              dados de menores. Se identificarmos que coletamos dados de um menor, excluiremos essas informações.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Alterações nesta Política</h2>
            <p>
              Podemos atualizar esta Política de Privacidade periodicamente. Notificaremos sobre mudanças 
              significativas por e-mail ou através do aplicativo. Recomendamos revisar esta página regularmente.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Contato</h2>
            <p>
              Para dúvidas sobre esta Política de Privacidade ou sobre seus dados pessoais, entre em contato:
            </p>
            <p className="mt-2">
              <strong>E-mail:</strong> {APP_INFO.email}<br />
              <strong>Aplicativo:</strong> {APP_INFO.name}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Terms of Use Page
const TermsOfUsePage = ({ onBack }) => {
  return (
    <div className="fade-in max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="glass-panel rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <ScrollText className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Termos de Uso</h1>
            <p className="text-sm text-zinc-500">Última atualização: {APP_INFO.lastUpdate}</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-zinc-300">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Aceitação dos Termos</h2>
            <p>
              Ao acessar ou usar o <strong>{APP_INFO.name}</strong>, você concorda com estes Termos de Uso. 
              Se não concordar com algum termo, não utilize nosso serviço.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Descrição do Serviço</h2>
            <p>
              O {APP_INFO.name} é uma ferramenta que utiliza Inteligência Artificial para:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Detectar se textos foram gerados por IA</li>
              <li>Identificar imagens criadas artificialmente</li>
              <li>Analisar áudios para detectar vozes sintéticas</li>
              <li>Verificar vídeos em busca de deepfakes</li>
              <li>Identificar padrões de golpes e fraudes (Golpe Alert)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. Cadastro e Conta</h2>
            <p>Para usar o serviço, você deve:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Ter pelo menos 18 anos de idade</li>
              <li>Fornecer informações verdadeiras no cadastro</li>
              <li>Manter a confidencialidade da sua senha</li>
              <li>Ser responsável por todas as atividades em sua conta</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Sistema de Créditos</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Novos usuários recebem 10 créditos gratuitos</li>
              <li>Créditos adicionais podem ser adquiridos através do aplicativo</li>
              <li>Cada tipo de análise consome uma quantidade específica de créditos</li>
              <li>Créditos não são reembolsáveis, exceto em casos previstos em lei</li>
              <li>Créditos não expiram enquanto a conta estiver ativa</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Uso Aceitável</h2>
            <p>Você concorda em NÃO utilizar o serviço para:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Atividades ilegais ou fraudulentas</li>
              <li>Enviar conteúdo ilegal, ofensivo ou que viole direitos de terceiros</li>
              <li>Tentar burlar os sistemas de segurança</li>
              <li>Fazer engenharia reversa do serviço</li>
              <li>Revender ou redistribuir o serviço sem autorização</li>
              <li>Sobrecarregar nossos sistemas intencionalmente</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Limitações do Serviço</h2>
            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-4">
              <p className="text-amber-300">
                <strong>Importante:</strong> O {APP_INFO.name} é uma ferramenta auxiliar e não garante 100% de precisão. 
                Os resultados devem ser usados como referência, não como verdade absoluta.
              </p>
            </div>
            <ul className="list-disc pl-6 space-y-2">
              <li>A tecnologia de IA está em constante evolução</li>
              <li>Textos curtos podem gerar resultados menos precisos</li>
              <li>O serviço não substitui o julgamento humano</li>
              <li>Não nos responsabilizamos por decisões tomadas com base nos resultados</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Propriedade Intelectual</h2>
            <p>
              Todo o conteúdo do {APP_INFO.name}, incluindo logo, design, código e algoritmos, 
              é de nossa propriedade ou licenciado para nós. Você não pode copiar, modificar ou 
              distribuir nosso conteúdo sem autorização.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Isenção de Garantias</h2>
            <p>
              O serviço é fornecido "como está", sem garantias de qualquer tipo. Não garantimos que:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>O serviço será ininterrupto ou livre de erros</li>
              <li>Os resultados serão 100% precisos</li>
              <li>O serviço atenderá todas as suas necessidades</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Limitação de Responsabilidade</h2>
            <p>
              Na máxima extensão permitida por lei, não seremos responsáveis por quaisquer danos 
              indiretos, incidentais, especiais ou consequentes decorrentes do uso do serviço.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Modificações do Serviço</h2>
            <p>
              Reservamo-nos o direito de modificar, suspender ou descontinuar o serviço a qualquer momento, 
              com ou sem aviso prévio. Também podemos atualizar estes Termos, notificando os usuários sobre mudanças significativas.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">11. Rescisão</h2>
            <p>
              Podemos suspender ou encerrar sua conta se você violar estes Termos. 
              Você pode encerrar sua conta a qualquer momento entrando em contato conosco.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">12. Lei Aplicável</h2>
            <p>
              Estes Termos são regidos pelas leis da República Federativa do Brasil. 
              Qualquer disputa será resolvida no foro da comarca de sua residência.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">13. Contato</h2>
            <p>
              Para dúvidas sobre estes Termos de Uso:<br />
              <strong>E-mail:</strong> {APP_INFO.email}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Disclaimer Page
const DisclaimerPage = ({ onBack }) => {
  return (
    <div className="fade-in max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="glass-panel rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Aviso de Precisão</h1>
            <p className="text-sm text-zinc-500">Disclaimer - Limitações do Serviço</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-zinc-300">
          <div className="p-6 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <h2 className="text-xl font-semibold text-amber-300 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Aviso Importante
            </h2>
            <p className="text-amber-200">
              O <strong>{APP_INFO.name}</strong> é uma <strong>ferramenta auxiliar</strong> de detecção e 
              <strong> não deve ser usado como única fonte</strong> para tomar decisões importantes. 
              Os resultados são indicativos e não garantem 100% de precisão.
            </p>
          </div>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Sobre a Tecnologia</h2>
            <p>
              Nosso sistema utiliza modelos avançados de Inteligência Artificial (GPT-5.2 e Gemini) 
              para analisar conteúdos. No entanto, é importante entender que:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>A tecnologia de IA está em constante evolução</li>
              <li>Novos métodos de geração de conteúdo surgem frequentemente</li>
              <li>Nenhum sistema de detecção é perfeito</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Limitações Conhecidas</h2>
            
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-cyan-400 mb-2">Textos</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Textos curtos (menos de 2 parágrafos) podem ter análise menos precisa</li>
                  <li>IAs conversacionais modernas imitam bem a linguagem humana informal</li>
                  <li>Textos editados por humanos após geração por IA são difíceis de detectar</li>
                </ul>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-purple-400 mb-2">Imagens</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Imagens muito comprimidas perdem detalhes importantes</li>
                  <li>Fotos editadas podem ser confundidas com geradas por IA</li>
                  <li>Novas técnicas de geração podem não ser detectadas inicialmente</li>
                </ul>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-emerald-400 mb-2">Áudios</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Áudios de baixa qualidade dificultam a análise</li>
                  <li>Vozes clonadas de alta qualidade são desafiadoras</li>
                  <li>Ruídos de fundo podem interferir na análise</li>
                </ul>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-rose-400 mb-2">Vídeos</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Vídeos curtos oferecem menos dados para análise</li>
                  <li>Deepfakes sofisticados podem passar despercebidos</li>
                  <li>A análise é baseada em frames, podendo perder detalhes</li>
                </ul>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-orange-400 mb-2">Golpe Alert</h3>
                <ul className="list-disc pl-6 space-y-1 text-sm">
                  <li>Novos tipos de golpes podem não estar em nosso padrão</li>
                  <li>Mensagens legítimas podem usar linguagem similar a golpes</li>
                  <li>Golpistas adaptam suas técnicas constantemente</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Recomendações</h2>
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Use como segunda opinião:</strong> Combine nossos resultados com seu próprio julgamento</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Verifique a fonte:</strong> Sempre que possível, confirme a origem do conteúdo</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Desconfie de urgência:</strong> Golpes geralmente pressionam para ação imediata</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Confirme por outros meios:</strong> Ligue para a pessoa, verifique o site oficial</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span><strong>Em caso de dúvida, não aja:</strong> É melhor verificar do que se arrepender</span>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Responsabilidade</h2>
            <p>
              O {APP_INFO.name} não se responsabiliza por:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Decisões tomadas com base exclusivamente em nossos resultados</li>
              <li>Perdas financeiras decorrentes de golpes não detectados</li>
              <li>Falsos positivos que gerem constrangimentos</li>
              <li>Uso indevido do serviço</li>
            </ul>
          </section>

          <section className="pt-4 border-t border-zinc-700">
            <p className="text-sm text-zinc-500 text-center">
              Ao usar o {APP_INFO.name}, você reconhece ter lido e compreendido estas limitações.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// LGPD Page
const LGPDPage = ({ onBack }) => {
  return (
    <div className="fade-in max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="glass-panel rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
            <Scale className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">LGPD - Lei Geral de Proteção de Dados</h1>
            <p className="text-sm text-zinc-500">Lei nº 13.709/2018 - Seus Direitos</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-zinc-300">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">O que é a LGPD?</h2>
            <p>
              A <strong>Lei Geral de Proteção de Dados Pessoais (LGPD)</strong> é a legislação brasileira que 
              regula as atividades de tratamento de dados pessoais. Ela estabelece regras sobre coleta, 
              armazenamento, tratamento e compartilhamento de dados pessoais, impondo mais proteção e 
              penalidades para o não cumprimento.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Seus Direitos como Titular dos Dados</h2>
            <p>Conforme a LGPD (Art. 18), você tem os seguintes direitos:</p>
            
            <div className="space-y-3 mt-4">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">1. Confirmação e Acesso</h3>
                <p className="text-sm">Você pode solicitar a confirmação da existência de tratamento de seus dados e ter acesso a eles.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">2. Correção</h3>
                <p className="text-sm">Você pode solicitar a correção de dados incompletos, inexatos ou desatualizados.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">3. Anonimização, Bloqueio ou Eliminação</h3>
                <p className="text-sm">Você pode solicitar a anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">4. Portabilidade</h3>
                <p className="text-sm">Você pode solicitar a portabilidade dos seus dados para outro fornecedor de serviço.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">5. Eliminação</h3>
                <p className="text-sm">Você pode solicitar a eliminação dos dados pessoais tratados com seu consentimento.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">6. Informação sobre Compartilhamento</h3>
                <p className="text-sm">Você pode obter informações sobre as entidades públicas e privadas com as quais compartilhamos seus dados.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">7. Informação sobre Consentimento</h3>
                <p className="text-sm">Você tem direito a informação sobre a possibilidade de não fornecer consentimento e suas consequências.</p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-green-400 mb-2">8. Revogação do Consentimento</h3>
                <p className="text-sm">Você pode revogar o consentimento a qualquer momento, mediante manifestação expressa.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Base Legal para Tratamento</h2>
            <p>O {APP_INFO.name} trata seus dados com base nas seguintes hipóteses legais (Art. 7º da LGPD):</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong>Consentimento:</strong> Para criar sua conta e utilizar nossos serviços</li>
              <li><strong>Execução de Contrato:</strong> Para fornecer os serviços de detecção de IA</li>
              <li><strong>Legítimo Interesse:</strong> Para melhorar nossos algoritmos e prevenir fraudes</li>
              <li><strong>Cumprimento de Obrigação Legal:</strong> Quando exigido por lei</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Dados Sensíveis</h2>
            <p>
              O {APP_INFO.name} <strong>não coleta dados sensíveis</strong> de forma intencional (origem racial, 
              convicção religiosa, opinião política, dados de saúde, etc.). O conteúdo que você envia para 
              análise é processado temporariamente e não é categorizado ou armazenado como dado sensível.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Transferência Internacional de Dados</h2>
            <p>
              Para fornecer o serviço de detecção, seus dados podem ser processados por provedores de IA 
              localizados fora do Brasil (OpenAI nos EUA, Google). Essa transferência é feita de acordo com 
              as regras da LGPD, garantindo nível adequado de proteção.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Encarregado de Dados (DPO)</h2>
            <p>
              Para exercer seus direitos ou esclarecer dúvidas sobre o tratamento de seus dados pessoais, 
              entre em contato com nosso Encarregado de Proteção de Dados:
            </p>
            <div className="mt-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
              <p><strong>E-mail:</strong> {APP_INFO.email}</p>
              <p><strong>Assunto:</strong> LGPD - [Seu Nome]</p>
              <p className="text-sm text-zinc-400 mt-2">
                Responderemos sua solicitação em até 15 dias, conforme determina a lei.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Como Exercer seus Direitos</h2>
            <p>Para exercer qualquer um dos seus direitos, você pode:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Enviar e-mail para <strong>{APP_INFO.email}</strong> com o assunto "LGPD"</li>
              <li>Especificar qual direito deseja exercer</li>
              <li>Fornecer dados para confirmar sua identidade</li>
              <li>Aguardar nossa resposta em até 15 dias</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Autoridade Nacional</h2>
            <p>
              Caso não fique satisfeito com nossa resposta, você pode apresentar reclamação à 
              Autoridade Nacional de Proteção de Dados (ANPD):
            </p>
            <div className="mt-3 p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <p><strong>Site:</strong> www.gov.br/anpd</p>
              <p className="text-sm text-zinc-400 mt-1">
                A ANPD é o órgão responsável por zelar pela proteção de dados pessoais no Brasil.
              </p>
            </div>
          </section>

          <section className="pt-4 border-t border-zinc-700">
            <p className="text-sm text-zinc-500 text-center">
              Este documento foi elaborado em conformidade com a Lei nº 13.709/2018 (LGPD).<br />
              Última atualização: {APP_INFO.lastUpdate}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Referral Program Legal Page
const ReferralProgramPage = ({ onBack }) => {
  return (
    <div className="fade-in max-w-4xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <div className="glass-panel rounded-xl p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
            <Users className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Programa de Indicação WISE EAGLE</h1>
            <p className="text-sm text-zinc-500">Regulamento e Natureza Jurídica</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-6 text-zinc-300">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Natureza do Programa</h2>
            <p>
              O Programa de Indicação da {APP_INFO.name} é uma iniciativa <strong>voluntária e opcional</strong> que 
              permite a qualquer pessoa recomendar os serviços da plataforma a terceiros, recebendo recompensas 
              por indicações bem-sucedidas. Este programa <strong>não constitui, em nenhuma hipótese, vínculo 
              empregatício, relação de trabalho, parceria comercial, sociedade, franquia ou representação 
              comercial</strong> entre o participante e a {APP_INFO.name}, nos termos da legislação brasileira vigente, 
              em especial os artigos 2º e 3º da CLT (Consolidação das Leis do Trabalho).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Ausência dos Requisitos de Vínculo Empregatício</h2>
            <p className="mb-4">
              Conforme o artigo 3º da CLT, o vínculo empregatício exige a presença simultânea de quatro requisitos: 
              pessoalidade, habitualidade, subordinação e onerosidade. O Programa de Indicação da {APP_INFO.name} 
              não preenche nenhum desses requisitos, conforme demonstrado a seguir:
            </p>

            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-cyan-400 mb-2">1. Ausência de Subordinação</h3>
                <p className="text-sm">
                  A {APP_INFO.name} <strong>não emite ordens, não estabelece metas obrigatórias, não define horários 
                  fixos e não exerce qualquer tipo de poder diretivo</strong> sobre os participantes do programa. 
                  A participação é inteiramente livre e espontânea, cabendo exclusivamente ao participante decidir 
                  se, quando e como deseja realizar indicações. Não há qualquer penalidade por inatividade ou por 
                  não atingir resultados.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-cyan-400 mb-2">2. Ausência de Habitualidade</h3>
                <p className="text-sm">
                  A {APP_INFO.name} <strong>recompensa por evento específico</strong> (indicação concretizada), e não 
                  mediante remuneração fixa, periódica ou mensal. Não há obrigação de frequência, continuidade ou 
                  regularidade na realização de indicações. O participante pode realizar uma única indicação ou 
                  múltiplas, a qualquer tempo, sem qualquer compromisso de periodicidade.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-cyan-400 mb-2">3. Ausência de Pessoalidade</h3>
                <p className="text-sm">
                  A {APP_INFO.name} <strong>permite que qualquer pessoa participe</strong> do programa de indicação, 
                  inclusive aqueles que não são clientes da plataforma. Entende-se por cliente toda pessoa que adquire 
                  algum produto ou serviço da {APP_INFO.name}. A indicação não é vinculada de forma personalíssima 
                  ao participante, não havendo exigência de que a atividade seja executada pessoalmente ou de forma 
                  intransferível.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <h3 className="font-semibold text-cyan-400 mb-2">4. Ausência de Exclusividade</h3>
                <p className="text-sm">
                  A {APP_INFO.name} <strong>não exige exclusividade</strong> de seus participantes. O usuário é livre 
                  para participar de programas de indicação de outras empresas, prestar serviços a terceiros ou 
                  exercer qualquer outra atividade profissional, sem qualquer restrição imposta pela {APP_INFO.name}.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Recompensas e Bonificações</h2>
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <h3 className="font-semibold text-amber-400 mb-2">Forma de Recompensa</h3>
                <p className="text-sm">
                  As recompensas concedidas pela {APP_INFO.name} são pagas exclusivamente em <strong>criptoativos 
                  (USDT ou USDC)</strong>, por meio da exchange parceira <strong>OASIS</strong>. As recompensas têm 
                  natureza de <strong>bonificação eventual</strong>, não configurando salário, remuneração ou 
                  contraprestação por serviços prestados.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <h3 className="font-semibold text-amber-400 mb-2">Resgate das Recompensas</h3>
                <p className="text-sm">
                  Assim que o participante acumular o valor mínimo estabelecido no regulamento do programa 
                  (atualmente <strong>$100.00 em USDT/USDC</strong>), poderá solicitar a transferência para sua 
                  carteira na exchange OASIS. A partir da exchange OASIS, o participante tem total liberdade para 
                  transferir seus criptoativos para qualquer outra carteira ou exchange de sua preferência.
                </p>
              </div>

              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <h3 className="font-semibold text-amber-400 mb-2">Estrutura de Bonificação</h3>
                <p className="text-sm">
                  O bônus é concedido conforme o participante avança na hierarquia de sua rede de indicação, 
                  de acordo com as regras estabelecidas no regulamento do programa. A estrutura multinível de 
                  bonificação (até 3 níveis) remunera apenas por indicações efetivas, não se confundindo com 
                  esquemas de pirâmide financeira, pois há comercialização real de produto/serviço.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Disposições Gerais</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>A {APP_INFO.name} reserva-se o direito de alterar, suspender ou encerrar o Programa de Indicação a qualquer momento, mediante aviso prévio aos participantes.</li>
              <li>A participação no programa implica na aceitação integral deste regulamento e dos Termos de Uso da plataforma.</li>
              <li>Qualquer tentativa de fraude, abuso ou manipulação do programa resultará no cancelamento da conta e perda das recompensas acumuladas.</li>
              <li>Este regulamento é regido pelas leis da República Federativa do Brasil, sendo eleito o foro da comarca do domicílio da {APP_INFO.name} para dirimir quaisquer controvérsias.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Fundamentação Legal</h2>
            <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700">
              <ul className="list-disc pl-6 space-y-2 text-sm">
                <li><strong>CLT - Art. 2º e 3º:</strong> Definição de empregador e empregado, requisitos do vínculo empregatício</li>
                <li><strong>Código Civil - Art. 593 a 609:</strong> Prestação de serviços</li>
                <li><strong>Lei nº 12.587/2012:</strong> Marco regulatório sobre programas de fidelidade e indicação</li>
                <li><strong>Código de Defesa do Consumidor:</strong> Transparência nas relações de consumo</li>
              </ul>
            </div>
          </section>

          <section className="pt-4 border-t border-zinc-700">
            <p className="text-sm text-zinc-500 text-center">
              Este documento foi elaborado para garantir a transparência e conformidade legal do 
              Programa de Indicação da {APP_INFO.name}.<br />
              Última atualização: {APP_INFO.lastUpdate}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

// Legal Hub Page (combines all legal pages)
const LegalPage = ({ onBack, initialSection = null }) => {
  const [activeSection, setActiveSection] = useState(initialSection);

  if (activeSection === 'privacy') {
    return <PrivacyPolicyPage onBack={() => setActiveSection(null)} />;
  }
  if (activeSection === 'terms') {
    return <TermsOfUsePage onBack={() => setActiveSection(null)} />;
  }
  if (activeSection === 'lgpd') {
    return <LGPDPage onBack={() => setActiveSection(null)} />;
  }
  if (activeSection === 'disclaimer') {
    return <DisclaimerPage onBack={() => setActiveSection(null)} />;
  }
  if (activeSection === 'referral-program') {
    return <ReferralProgramPage onBack={() => setActiveSection(null)} />;
  }

  return (
    <div className="fade-in max-w-2xl mx-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Voltar
      </button>

      <h1 className="text-2xl font-bold mb-6 text-center">Documentos Legais</h1>

      <div className="space-y-4">
        <button
          onClick={() => setActiveSection('privacy')}
          className="w-full p-6 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-cyan-500/50 transition-all flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Lock className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Política de Privacidade</h3>
            <p className="text-sm text-zinc-500">Como coletamos, usamos e protegemos seus dados</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </button>

        <button
          onClick={() => setActiveSection('terms')}
          className="w-full p-6 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-purple-500/50 transition-all flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
            <ScrollText className="w-6 h-6 text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Termos de Uso</h3>
            <p className="text-sm text-zinc-500">Regras e condições para usar o serviço</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </button>

        <button
          onClick={() => setActiveSection('lgpd')}
          className="w-full p-6 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-green-500/50 transition-all flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
            <Scale className="w-6 h-6 text-green-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">LGPD</h3>
            <p className="text-sm text-zinc-500">Lei Geral de Proteção de Dados - Seus Direitos</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </button>

        <button
          onClick={() => setActiveSection('disclaimer')}
          className="w-full p-6 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-amber-500/50 transition-all flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-amber-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Aviso de Precisão</h3>
            <p className="text-sm text-zinc-500">Limitações e recomendações do serviço</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </button>

        <button
          onClick={() => setActiveSection('referral-program')}
          className="w-full p-6 rounded-xl bg-zinc-800/50 border border-zinc-700 hover:border-cyan-500/50 transition-all flex items-center gap-4 text-left"
        >
          <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Users className="w-6 h-6 text-cyan-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">Programa de Indicação</h3>
            <p className="text-sm text-zinc-500">Regulamento, natureza jurídica e ausência de vínculo empregatício</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </button>
      </div>

      <p className="text-center text-sm text-zinc-600 mt-8">
        Última atualização: {APP_INFO.lastUpdate}
      </p>
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <div className="text-center mb-12">
      <h1 className="text-4xl md:text-5xl font-bold mb-4">
        Detecte <span className="text-gradient-cyan">Conteúdo Falso</span> e <span className="text-gradient-cyan">Ameaças Digitais</span>
      </h1>
      <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
        Verifique textos, imagens, áudios e vídeos criados por IA. Escaneie arquivos com mais de 70 antivírus.
        Proteja-se contra golpes, deepfakes, malwares e desinformação.
      </p>
    </div>
  );
};

// Main App Component
const MainApp = () => {
  const { user, token, loading, logout } = useAuth();
  const [showHistory, setShowHistory] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [showLegal, setShowLegal] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAuthPage, setShowAuthPage] = useState(false);
  const [activeTab, setActiveTab] = useState("text");
  const [checkScam, setCheckScam] = useState(true);

  // Check for referral code in URL - if present, show auth page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    
    if (refCode) {
      if (user) {
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        setShowAuthPage(true);
      }
    }
  }, [user]);

  // Check if user is admin
  useEffect(() => {
    const checkAdmin = async () => {
      if (user && token) {
        try {
          const res = await axios.get(`${API}/admin/check`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsAdmin(res.data.is_admin);
        } catch (err) {
          setIsAdmin(false);
        }
      }
    };
    checkAdmin();
  }, [user, token]);

  const resetAllPages = () => {
    setShowHistory(false);
    setShowCredits(false);
    setShowReferral(false);
    setShowLegal(false);
    setShowAdmin(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center hero-gradient">
        <Loader2 className="w-8 h-8 spinner text-cyan-500" />
      </div>
    );
  }

  // Show landing page or auth page if not logged in
  if (!user) {
    if (showAuthPage) {
      return <AuthPage onBackToLanding={() => setShowAuthPage(false)} />;
    }
    return <LandingPage onGetStarted={() => setShowAuthPage(true)} />;
  }

  return (
    <div className="app-container hero-gradient">
      <Header
        onShowHistory={() => { resetAllPages(); setShowHistory(true); }}
        onShowCredits={() => { resetAllPages(); setShowCredits(true); }}
        onShowReferral={() => { resetAllPages(); setShowReferral(true); }}
        onShowAdmin={() => { resetAllPages(); setShowAdmin(true); }}
        onBackToDashboard={() => { resetAllPages(); }}
        showingSubPage={showHistory || showCredits || showReferral || showLegal || showAdmin}
        isAdmin={isAdmin}
      />

      <main className="main-content">
        {showAdmin && isAdmin ? (
          <AdminPage onBack={() => setShowAdmin(false)} />
        ) : showLegal ? (
          <LegalPage onBack={() => setShowLegal(false)} />
        ) : showReferral ? (
          <ReferralPage onBack={() => setShowReferral(false)} />
        ) : showCredits ? (
          <CreditsPage onBack={() => setShowCredits(false)} />
        ) : showHistory ? (
          <HistoryPage onBack={() => setShowHistory(false)} />
        ) : (
          <>
            <HeroSection />

            {/* Tab Selector */}
            <div className="flex justify-center gap-2 md:gap-4 mb-4 flex-wrap">
              <button
                data-testid="tab-text"
                className={`tab-btn flex items-center gap-2 ${activeTab === 'text' ? 'active' : ''}`}
                onClick={() => setActiveTab('text')}
              >
                <FileText className="w-5 h-5" />
                <span className="hidden sm:inline">Verificar</span> Texto
              </button>
              <button
                data-testid="tab-image"
                className={`tab-btn flex items-center gap-2 ${activeTab === 'image' ? 'active' : ''}`}
                onClick={() => setActiveTab('image')}
              >
                <Image className="w-5 h-5" />
                <span className="hidden sm:inline">Verificar</span> Imagem
              </button>
              <button
                data-testid="tab-audio"
                className={`tab-btn flex items-center gap-2 ${activeTab === 'audio' ? 'active' : ''}`}
                onClick={() => setActiveTab('audio')}
              >
                <Mic className="w-5 h-5" />
                <span className="hidden sm:inline">Verificar</span> Áudio
              </button>
              <button
                data-testid="tab-video"
                className={`tab-btn flex items-center gap-2 ${activeTab === 'video' ? 'active' : ''}`}
                onClick={() => setActiveTab('video')}
              >
                <Video className="w-5 h-5" />
                <span className="hidden sm:inline">Verificar</span> Vídeo
              </button>
              <button
                data-testid="tab-link"
                className={`tab-btn flex items-center gap-2 ${activeTab === 'link' ? 'active' : ''}`}
                onClick={() => setActiveTab('link')}
              >
                <Link2 className="w-5 h-5" />
                <span className="hidden sm:inline">Verificar</span> Link
              </button>
              <button
                data-testid="tab-antivirus"
                className={`tab-btn flex items-center gap-2 ${activeTab === 'antivirus' ? 'active' : ''}`}
                onClick={() => setActiveTab('antivirus')}
              >
                <Bug className="w-5 h-5" />
                <span className="hidden sm:inline">Anti</span>vírus
              </button>
            </div>

            {/* Golpe Alert Toggle - Below tabs (for all types now) */}
            <div className="flex justify-center mb-8">
              <label className="flex items-center gap-3 px-5 py-3 rounded-xl bg-zinc-800/50 border border-zinc-700 cursor-pointer hover:border-orange-500/50 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center">
                  <ShieldAlert className="w-4 h-4 text-orange-400" />
                </div>
                <div>
                  <span className="text-sm font-medium text-zinc-200">Golpe Alert</span>
                  <p className="text-xs text-zinc-500">Detectar padrões de golpes</p>
                </div>
                <div className="relative ml-2">
                  <input
                    type="checkbox"
                    checked={checkScam}
                    onChange={(e) => setCheckScam(e.target.checked)}
                    className="sr-only peer"
                    data-testid="scam-toggle"
                  />
                  <div className="w-11 h-6 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </div>
              </label>
            </div>

            {/* Scanner Content */}
            <div className="max-w-2xl mx-auto">
              {activeTab === 'text' && <TextScanner checkScam={checkScam} />}
              {activeTab === 'image' && <ImageScanner checkScam={checkScam} />}
              {activeTab === 'audio' && <AudioScanner checkScam={checkScam} />}
              {activeTab === 'video' && <VideoScanner checkScam={checkScam} />}
              {activeTab === 'link' && <LinkScanner checkScam={checkScam} />}
              {activeTab === 'antivirus' && <AntivirusScanner />}
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-zinc-500">
              {APP_INFO.name} © 2026 - Proteja-se contra conteúdo gerado por IA
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
              <button
                onClick={() => { resetAllPages(); setShowLegal(true); }}
                className="text-zinc-500 hover:text-cyan-400 transition-colors"
                data-testid="footer-legal-btn"
              >
                Documentos Legais
              </button>
              <span className="text-zinc-700">|</span>
              <a 
                href={`mailto:${APP_INFO.email}`}
                className="text-zinc-500 hover:text-cyan-400 transition-colors"
              >
                Contato: {APP_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
